import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Tooltip,
  Autocomplete,
  Checkbox,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { tagopsService } from '../services/tagopsService';
import { getSubscriptionAccountLimits } from '../utils/subscriptionUtils';
import { useNotifications, createErrorNotification, createSuccessNotification, createWarningNotification } from './common/NotificationSystem';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionButton } from './common/PermissionButton';
import { useConfig, Account } from '../contexts/ConfigContext';

// Use the Account interface from ConfigContext

// AWS region codes mapped to display names: "Ireland (eu-west-1)"
const AWS_REGION_NAMES: Record<string, string> = {
  'us-east-1': 'N. Virginia',
  'us-east-2': 'Ohio',
  'us-west-1': 'N. California',
  'us-west-2': 'Oregon',
  'eu-west-1': 'Ireland',
  'eu-west-2': 'London',
  'eu-west-3': 'Paris',
  'eu-central-1': 'Frankfurt',
  'eu-central-2': 'Zurich',
  'eu-north-1': 'Stockholm',
  'eu-south-1': 'Milan',
  'eu-south-2': 'Spain',
  'ap-southeast-1': 'Singapore',
  'ap-southeast-2': 'Sydney',
  'ap-southeast-3': 'Jakarta',
  'ap-southeast-4': 'Melbourne',
  'ap-southeast-5': 'Malaysia',
  'ap-northeast-1': 'Tokyo',
  'ap-northeast-2': 'Seoul',
  'ap-northeast-3': 'Osaka',
  'ap-south-1': 'Mumbai',
  'ap-south-2': 'Hyderabad',
  'ap-east-1': 'Hong Kong',
  'ca-central-1': 'Canada Central',
  'sa-east-1': 'São Paulo',
  'af-south-1': 'Cape Town',
  'me-south-1': 'Bahrain',
  'me-central-1': 'UAE',
  'il-central-1': 'Tel Aviv',
  'mx-central-1': 'Mexico',
};

const AWS_REGIONS = Object.keys(AWS_REGION_NAMES).sort();

const formatRegionLabel = (regionCode: string): string => {
  const name = AWS_REGION_NAMES[regionCode];
  return name ? `${name} (${regionCode})` : regionCode;
};

export const AwsAccounts: React.FC = () => {
  const { showNotification } = useNotifications();
  const { accountsData, serviceConfig, loading: configLoading, error: configError, refreshAccounts } = useConfig();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [editVerifying, setEditVerifying] = useState(false);
  const [addingAccount, setAddingAccount] = useState(false);
  const [updatingAccount, setUpdatingAccount] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [verified, setVerified] = useState(false);
  const [editVerified, setEditVerified] = useState(false);
  const [cloudtrailEnabled, setCloudtrailEnabled] = useState<boolean | null>(null);
  const [editCloudtrailEnabled, setEditCloudtrailEnabled] = useState<boolean | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCloudFormationDialog, setShowCloudFormationDialog] = useState(false);
  const [showAccountDetailsDialog, setShowAccountDetailsDialog] = useState(false);
  const [selectedAccountForDetails, setSelectedAccountForDetails] = useState<Account | null>(null);
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null);
  const [accountToDelete, setAccountToDelete] = useState<Account | null>(null);
  const [deletedAccount, setDeletedAccount] = useState<Account | null>(null);
  const [creatingAccounts, setCreatingAccounts] = useState<Map<string, string>>(new Map());
  const [updatingAccounts, setUpdatingAccounts] = useState<Map<string, string>>(new Map());
  const [deletingAccounts, setDeletingAccounts] = useState<Map<string, string>>(new Map());
  const [refreshing, setRefreshing] = useState(false);
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [maxAccounts, setMaxAccounts] = useState<number | null>(null);
  const [accountFormData, setAccountFormData] = useState({
    accountName: '',
    accountId: '',
    roleName: 'tagops-exec-account-role',
    externalId: generateRandomId(),
    mainRegion: '' as string,
    regions: [] as string[],
  });
  
  // Organization state
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [verifyingOrg, setVerifyingOrg] = useState(false);
  const [editVerifyingOrg, setEditVerifyingOrg] = useState(false);
  const [addingOrg, setAddingOrg] = useState(false);
  const [updatingOrg, setUpdatingOrg] = useState(false);
  const [deletingOrg, setDeletingOrg] = useState(false);
  const [verifiedOrg, setVerifiedOrg] = useState(false);
  const [editVerifiedOrg, setEditVerifiedOrg] = useState(false);
  const [showAddOrgDialog, setShowAddOrgDialog] = useState(false);
  const [showEditOrgDialog, setShowEditOrgDialog] = useState(false);
  const [showDeleteOrgDialog, setShowDeleteOrgDialog] = useState(false);
  const [currentOrg, setCurrentOrg] = useState<any | null>(null);
  const [orgToDelete, setOrgToDelete] = useState<any | null>(null);
  const [deletedOrganization, setDeletedOrganization] = useState<any | null>(null);
  const [showCloudFormationOrgDialog, setShowCloudFormationOrgDialog] = useState(false);
  const [creatingOrganizations, setCreatingOrganizations] = useState<Map<string, string>>(new Map());
  const [deletingOrganizations, setDeletingOrganizations] = useState<Map<string, string>>(new Map());
  const [orgMembers, setOrgMembers] = useState<Record<string, string[]>>({});
  const [loadingOrgMembers, setLoadingOrgMembers] = useState<Record<string, boolean>>({});
  const [expandedOrganizations, setExpandedOrganizations] = useState<Record<string, boolean>>({});
  const [selectedMembers, setSelectedMembers] = useState<Record<string, Set<string>>>({});
  const [verifyingMember, setVerifyingMember] = useState<Record<string, boolean>>({});
  const [verifiedMembers, setVerifiedMembers] = useState<Record<string, boolean>>({});
  const [memberCloudTrailStatus, setMemberCloudTrailStatus] = useState<Record<string, boolean | null>>({});
  const [addingMemberAccount, setAddingMemberAccount] = useState<Record<string, boolean>>({});
  const [bulkAddingMembers, setBulkAddingMembers] = useState<Record<string, boolean>>({});
  const [bulkVerifyingMembers, setBulkVerifyingMembers] = useState<Record<string, boolean>>({});
  const [bulkDeletingMembers, setBulkDeletingMembers] = useState<Record<string, boolean>>({});
  const [memberFormData, setMemberFormData] = useState<Record<string, {
    accountId: string;
    accountName: string;
    roleName: string;
    externalId: string;
    regions: string[];
    organizationId: string;
  }>>({});
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [currentMemberAccountId, setCurrentMemberAccountId] = useState<string | null>(null);
  const [orgFormData, setOrgFormData] = useState({
    organizationName: '',
    accountId: '',
    roleName: 'tagops-exec-role',
    externalId: generateRandomId(),
    regions: [] as string[],
    organizationalUnitIds: '',
    includeManagementAccount: 'false',
  });
  
  const [orgValidationErrors, setOrgValidationErrors] = useState({
    organizationName: '',
    accountId: '',
    roleName: '',
    externalId: '',
  });
  
  const [orgFieldTouched, setOrgFieldTouched] = useState({
    organizationName: false,
    accountId: false,
    roleName: false,
    externalId: false,
  });
  
  const [validationErrors, setValidationErrors] = useState({
    accountName: '',
    accountId: '',
    roleName: '',
    externalId: '',
  });
  
  const [fieldTouched, setFieldTouched] = useState({
    accountName: false,
    accountId: false,
    roleName: false,
    externalId: false,
  });
  
  function generateRandomId(length = 10) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }

  // Validation functions
  const validateAccountName = (name: string): string => {
    if (!name || name.trim().length === 0) {
      return "Account name is required";
    }
    if (name.length > 50) {
      return "Account name must be 50 characters or less";
    }
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(name)) {
      return "Account name can only contain letters, numbers, spaces, hyphens, and underscores";
    }
    return "";
  };

  const validateAccountId = (id: string): string => {
    if (!id || id.trim().length === 0) {
      return "Account ID is required";
    }
    if (!/^\d{12}$/.test(id)) {
      return "Account ID must be exactly 12 digits";
    }
    return "";
  };

  const validateRoleName = (name: string): string => {
    if (!name || name.trim().length === 0) {
      return "IAM role name is required";
    }
    if (name.length > 64) {
      return "IAM role name must be 64 characters or less";
    }
    if (!/^[a-zA-Z0-9_+=,.@-]+$/.test(name)) {
      return "IAM role name can only contain letters, numbers, and _+=,.@- characters";
    }
    if (/\s/.test(name)) {
      return "IAM role name cannot contain spaces";
    }
    return "";
  };

  const validateExternalId = (id: string): string => {
    if (!id || id.trim().length === 0) {
      return "External ID is required";
    }
    if (id.length < 2) {
      return "External ID must be at least 2 characters";
    }
    if (id.length > 256) {
      return "External ID must be 256 characters or less";
    }
    return "";
  };

  // Real-time validation function
  const validateField = (fieldName: string, value: string, markTouched: boolean = true) => {
    let error = "";
    switch (fieldName) {
      case 'accountName':
        error = validateAccountName(value);
        break;
      case 'accountId':
        error = validateAccountId(value);
        break;
      case 'roleName':
        error = validateRoleName(value);
        break;
      case 'externalId':
        error = validateExternalId(value);
        break;
    }
    
    if (markTouched) {
      setFieldTouched(prev => ({
        ...prev,
        [fieldName]: true
      }));
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    
    return error === "";
  };

  // Check if form is valid (no validation errors and all required fields filled)
  const isFormValid = () => {
    const hasNoErrors = !validationErrors.accountName && !validationErrors.accountId && 
                        !validationErrors.roleName && !validationErrors.externalId;
    const hasAllFields = accountFormData.accountName.trim() && accountFormData.accountId.trim() && 
                        accountFormData.roleName.trim() && accountFormData.externalId.trim() &&
                        !!accountFormData.mainRegion.trim();
    return hasNoErrors && hasAllFields;
  };

  // Check if edit form is valid (no validation errors for editable fields)
  const isEditFormValid = () => {
    const hasNoErrors = !validationErrors.accountName && !validationErrors.roleName && !validationErrors.externalId;
    const hasAllFields = accountFormData.accountName.trim() && accountFormData.roleName.trim() && 
                        accountFormData.externalId.trim();
    return hasNoErrors && hasAllFields;
  };

  // Organization validation functions
  const validateOrganizationName = (name: string): string => {
    if (!name || name.trim().length === 0) {
      return "Organization name is required";
    }
    if (name.length > 100) {
      return "Organization name must be 100 characters or less";
    }
    return "";
  };

  // Real-time validation function for organization
  const validateOrgField = (fieldName: string, value: string, markTouched: boolean = true) => {
    let error = "";
    switch (fieldName) {
      case 'organizationName':
        error = validateOrganizationName(value);
        break;
      case 'accountId':
        error = validateAccountId(value);
        break;
      case 'roleName':
        error = validateRoleName(value);
        break;
      case 'externalId':
        error = validateExternalId(value);
        break;
    }
    
    if (markTouched) {
      setOrgFieldTouched(prev => ({
        ...prev,
        [fieldName]: true
      }));
    }
    
    setOrgValidationErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    
    return error === "";
  };

  // Check if organization form is valid
  const isOrgFormValid = () => {
    const hasNoErrors = !orgValidationErrors.organizationName && !orgValidationErrors.accountId && 
                        !orgValidationErrors.roleName && !orgValidationErrors.externalId;
    const hasAllFields = orgFormData.organizationName.trim() && orgFormData.accountId.trim() && 
                        orgFormData.roleName.trim() && orgFormData.externalId.trim();
    return hasNoErrors && hasAllFields;
  };

  // Check if edit organization form is valid
  const isEditOrgFormValid = () => {
    const hasNoErrors = !orgValidationErrors.organizationName && !orgValidationErrors.roleName && !orgValidationErrors.externalId;
    const hasAllFields = orgFormData.organizationName.trim() && orgFormData.roleName.trim() && 
                        orgFormData.externalId.trim();
    return hasNoErrors && hasAllFields;
  };


  // Load organizations
  const loadOrganizations = async (forceReloadMembers = false) => {
    setLoadingOrganizations(true);
    try {
      const response = await tagopsService.getOrganizations();
      const orgs = response.organizations || [];
      setOrganizations(orgs);
      
      // Automatically load members for each organization
      for (const org of orgs) {
        const accountId = org.organizationManagementAccountId || org.type?.split('#')[1] || '';
        if (accountId && (forceReloadMembers || !orgMembers[accountId])) {
          try {
            const membersResponse = await tagopsService.getOrganizationMembers(accountId);
            setOrgMembers(prev => ({ ...prev, [accountId]: membersResponse.members || [] }));
          } catch (error) {
            console.error(`Error loading members for organization ${accountId}:`, error);
            setOrgMembers(prev => ({ ...prev, [accountId]: [] }));
          }
        }
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
      showNotification(createErrorNotification(error));
    } finally {
      setLoadingOrganizations(false);
    }
  };

  // Load organizations on mount first, then load accounts
  useEffect(() => {
    const initializeData = async () => {
      // First load organizations
      await loadOrganizations();
      // Organizations are now loaded, accounts will be set when accountsData is available
    };
    initializeData();
  }, []);

  // Update accounts data when context data changes, but only after organizations are loaded
  useEffect(() => {
    if (accountsData && !loadingOrganizations) {
      // Only set accounts after organizations have finished loading
      // This ensures proper filtering of organization member accounts
      setAccounts(accountsData.accounts || []);
      setLoading(false);
    } else if (accountsData && loadingOrganizations) {
      // If organizations are still loading, keep loading state true
      setLoading(true);
    }
  }, [accountsData, loadingOrganizations]);

  // Toggle expand/collapse organization members
  const toggleOrganizationExpansion = async (accountId: string) => {
    const isCurrentlyExpanded = expandedOrganizations[accountId];
    
    // Toggle expand/collapse
    setExpandedOrganizations(prev => ({
      ...prev,
      [accountId]: !isCurrentlyExpanded
    }));
    
    // If expanding and members not loaded, load them
    if (!isCurrentlyExpanded && !orgMembers[accountId]) {
      setLoadingOrgMembers(prev => ({ ...prev, [accountId]: true }));
      try {
        const response = await tagopsService.getOrganizationMembers(accountId);
        setOrgMembers(prev => ({ ...prev, [accountId]: response.members || [] }));
      } catch (error) {
        console.error('Error loading organization members:', error);
        setOrgMembers(prev => ({ ...prev, [accountId]: [] }));
      } finally {
        setLoadingOrgMembers(prev => ({ ...prev, [accountId]: false }));
      }
    }
  };

  // Handle member checkbox selection
  const handleMemberSelection = (orgAccountId: string, memberAccountId: string, checked: boolean) => {
    setSelectedMembers(prev => {
      const newSelected = { ...prev };
      if (!newSelected[orgAccountId]) {
        newSelected[orgAccountId] = new Set<string>();
      }
      if (checked) {
        newSelected[orgAccountId].add(memberAccountId);
      } else {
        newSelected[orgAccountId].delete(memberAccountId);
      }
      return newSelected;
    });
  };

  // Handle select all members (including onboarded accounts)
  const handleSelectAllMembers = (orgAccountId: string, checked: boolean) => {
    const members = orgMembers[orgAccountId] || [];
    setSelectedMembers(prev => {
      const newSelected = { ...prev };
      if (checked) {
        // Select all members (both onboarded and non-onboarded)
        newSelected[orgAccountId] = new Set(members);
      } else {
        newSelected[orgAccountId] = new Set<string>();
      }
      return newSelected;
    });
  };

  // Check if all members are selected (including onboarded accounts)
  const areAllMembersSelected = (orgAccountId: string): boolean => {
    const members = orgMembers[orgAccountId] || [];
    const selected = selectedMembers[orgAccountId] || new Set<string>();
    return members.length > 0 && members.every(memberId => selected.has(memberId));
  };

  // Check if some members are selected
  const areSomeMembersSelected = (orgAccountId: string): boolean => {
    const members = orgMembers[orgAccountId] || [];
    const selected = selectedMembers[orgAccountId] || new Set<string>();
    return selected.size > 0 && selected.size < members.length;
  };

  // Bulk verify selected member accounts
  const handleBulkVerifyMembers = async (orgAccountId: string) => {
    const selected = selectedMembers[orgAccountId] || new Set<string>();
    if (selected.size === 0) {
      showNotification(createErrorNotification(new Error('Please select at least one member account to verify')));
      return;
    }

    const members = Array.from(selected);
    setBulkVerifyingMembers(prev => ({ ...prev, [orgAccountId]: true }));

    try {
      // Get organization details
      const org = organizations.find(o => {
        const id = o.organizationManagementAccountId || o.type?.split('#')[1] || '';
        return id === orgAccountId;
      });

      if (!org) {
        showNotification(createErrorNotification(new Error('Organization not found')));
        setBulkVerifyingMembers(prev => ({ ...prev, [orgAccountId]: false }));
        return;
      }

      // Remove "-org" suffix from organization role_name for member accounts
      const memberRoleName = org.role_name?.endsWith('-org') 
        ? org.role_name.slice(0, -4) 
        : org.role_name;

      if (!memberRoleName || !org.externalId) {
        showNotification(createErrorNotification(new Error('Organization is missing required role name or external ID')));
        setBulkVerifyingMembers(prev => ({ ...prev, [orgAccountId]: false }));
        return;
      }

      let successCount = 0;
      let failCount = 0;
      let cloudtrailDisabledCount = 0;
      const failedAccounts: string[] = [];
      const cloudtrailDisabledAccounts: string[] = [];

      // Verify all selected accounts in parallel (including onboarded accounts)
      const verifyPromises = members.map(async (memberAccountId) => {
        setVerifyingMember(prev => ({ ...prev, [memberAccountId]: true }));
        try {
          const response = await tagopsService.verifyAccount(
            memberAccountId,
            memberRoleName,
            org.externalId
          ) as { verified: boolean; message: string; cloudtrail_enabled?: boolean };
          
          if (response && response.verified) {
            setVerifiedMembers(prev => ({ ...prev, [memberAccountId]: true }));
            // Store CloudTrail status
            const cloudtrailEnabled = response.cloudtrail_enabled ?? null;
            setMemberCloudTrailStatus(prev => ({ ...prev, [memberAccountId]: cloudtrailEnabled }));
            
            // Track CloudTrail disabled accounts
            if (cloudtrailEnabled === false) {
              cloudtrailDisabledCount++;
              cloudtrailDisabledAccounts.push(memberAccountId);
            }
            
            // Initialize form data for this member (only if not already onboarded)
            if (!isMemberOnboarded(memberAccountId)) {
              const organizationManagementAccountId = org.organizationManagementAccountId || org.type?.split('#')[1] || orgAccountId;
              setMemberFormData(prev => ({
                ...prev,
                [memberAccountId]: {
                  accountId: memberAccountId,
                  accountName: `Account ${memberAccountId}`,
                  roleName: memberRoleName,
                  externalId: org.externalId,
                  regions: Array.isArray(org.regions) ? org.regions : (org.regions ? JSON.parse(org.regions) : []),
                  organizationId: organizationManagementAccountId,
                }
              }));
            }
            return { accountId: memberAccountId, success: true, cloudtrailEnabled, message: response.message || '' };
          } else {
            setVerifiedMembers(prev => ({ ...prev, [memberAccountId]: false }));
            setMemberCloudTrailStatus(prev => ({ ...prev, [memberAccountId]: null }));
            failedAccounts.push(memberAccountId);
            return { accountId: memberAccountId, success: false, cloudtrailEnabled: null, message: response?.message || 'Verification failed' };
          }
        } catch (error: any) {
          console.error(`Error verifying member account ${memberAccountId}:`, error);
          setVerifiedMembers(prev => ({ ...prev, [memberAccountId]: false }));
          setMemberCloudTrailStatus(prev => ({ ...prev, [memberAccountId]: null }));
          failedAccounts.push(memberAccountId);
          const errorMessage = error?.response?.data?.error?.message || error?.message || 'Unknown error';
          return { accountId: memberAccountId, success: false, cloudtrailEnabled: null, message: errorMessage };
        } finally {
          setVerifyingMember(prev => ({ ...prev, [memberAccountId]: false }));
        }
      });

      const results = await Promise.all(verifyPromises);
      
      results.forEach(result => {
        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }
      });


      // Build detailed notification message
      let notificationTitle = 'Member Accounts Verified';
      let notificationMessage = '';
      let notificationType: 'success' | 'warning' | 'error' = 'success';

      if (successCount > 0 && failCount === 0) {
        // All succeeded
        notificationMessage = `Successfully verified ${successCount} member account${successCount === 1 ? '' : 's'}.`;
        if (cloudtrailDisabledCount > 0) {
          notificationType = 'warning';
          notificationTitle = 'Verification Complete with CloudTrail Warning';
          notificationMessage += ` ${cloudtrailDisabledCount} account${cloudtrailDisabledCount === 1 ? '' : 's'} ${cloudtrailDisabledCount === 1 ? 'has' : 'have'} CloudTrail disabled, which may affect event-based tagging features.`;
          if (cloudtrailDisabledAccounts.length > 0 && cloudtrailDisabledAccounts.length <= 5) {
            notificationMessage += ` Affected accounts: ${cloudtrailDisabledAccounts.join(', ')}.`;
          }
        }
      } else if (successCount > 0 && failCount > 0) {
        // Some succeeded, some failed
        notificationType = cloudtrailDisabledCount > 0 ? 'warning' : 'success';
        notificationTitle = failCount > 0 ? 'Partial Verification Results' : 'Verification Complete with CloudTrail Warning';
        notificationMessage = `Successfully verified ${successCount} account${successCount === 1 ? '' : 's'}.`;
        if (failCount > 0) {
          notificationMessage += ` ${failCount} account${failCount === 1 ? '' : 's'} failed to verify.`;
          if (failedAccounts.length > 0 && failedAccounts.length <= 5) {
            notificationMessage += ` Failed accounts: ${failedAccounts.join(', ')}.`;
          }
        }
        if (cloudtrailDisabledCount > 0) {
          notificationMessage += ` ${cloudtrailDisabledCount} account${cloudtrailDisabledCount === 1 ? '' : 's'} ${cloudtrailDisabledCount === 1 ? 'has' : 'have'} CloudTrail disabled.`;
          if (cloudtrailDisabledAccounts.length > 0 && cloudtrailDisabledAccounts.length <= 5) {
            notificationMessage += ` Affected accounts: ${cloudtrailDisabledAccounts.join(', ')}.`;
          }
        }
      } else if (failCount > 0) {
        // All failed
        notificationType = 'error';
        notificationTitle = 'Verification Failed';
        notificationMessage = `Failed to verify ${failCount} member account${failCount === 1 ? '' : 's'}.`;
        if (failedAccounts.length > 0 && failedAccounts.length <= 5) {
          notificationMessage += ` Failed accounts: ${failedAccounts.join(', ')}.`;
        } else if (failedAccounts.length > 5) {
          notificationMessage += ` First 5 failed accounts: ${failedAccounts.slice(0, 5).join(', ')}.`;
        }
      }

      // Show appropriate notification
      if (notificationType === 'error') {
        showNotification(createErrorNotification(new Error(notificationMessage)));
      } else if (notificationType === 'warning') {
        showNotification(createWarningNotification(notificationTitle, notificationMessage));
      } else {
        showNotification(createSuccessNotification(notificationTitle, notificationMessage));
      }
    } catch (error) {
      console.error('Error in bulk verify:', error);
      showNotification(createErrorNotification(error));
    } finally {
      setBulkVerifyingMembers(prev => ({ ...prev, [orgAccountId]: false }));
    }
  };

  // Bulk delete selected onboarded member accounts
  const handleBulkDeleteMembers = async (orgAccountId: string) => {
    const selected = selectedMembers[orgAccountId] || new Set<string>();
    if (selected.size === 0) {
      showNotification(createErrorNotification(new Error('Please select at least one member account to delete')));
      return;
    }

    // Filter to only onboarded accounts
    const onboardedSelected = Array.from(selected).filter(memberAccountId => {
      const account = getOnboardedMemberAccount(memberAccountId);
      return account !== null;
    });

    if (onboardedSelected.length === 0) {
      showNotification(createErrorNotification(new Error('Please select at least one onboarded account to delete')));
      return;
    }

    // Confirm deletion
    const confirmMessage = `Are you sure you want to delete ${onboardedSelected.length} onboarded account${onboardedSelected.length === 1 ? '' : 's'}? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setBulkDeletingMembers(prev => ({ ...prev, [orgAccountId]: true }));

    try {
      let successCount = 0;
      let failCount = 0;

      for (const memberAccountId of onboardedSelected) {
        try {
          await tagopsService.deleteAccount({ accountId: memberAccountId });
          successCount++;
        } catch (error) {
          console.error(`Error deleting member account ${memberAccountId}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        showNotification(createSuccessNotification(
          'Member Accounts Deleted',
          `Successfully deleted ${successCount} member account${successCount === 1 ? '' : 's'}.${failCount > 0 ? ` ${failCount} account${failCount === 1 ? '' : 's'} failed to delete.` : ''}`
        ));
      } else {
        showNotification(createErrorNotification(new Error(`Failed to delete ${failCount} member account${failCount === 1 ? '' : 's'}`)));
      }

      // Clear selections
      setSelectedMembers(prev => {
        const newSelected = { ...prev };
        newSelected[orgAccountId] = new Set<string>();
        return newSelected;
      });

      await refreshAccounts();
    } catch (error) {
      showNotification(createErrorNotification(error));
    } finally {
      setBulkDeletingMembers(prev => ({ ...prev, [orgAccountId]: false }));
    }
  };

  // Bulk add selected member accounts
  const handleBulkAddMembers = async (orgAccountId: string) => {
    const selected = selectedMembers[orgAccountId] || new Set<string>();
    if (selected.size === 0) {
      showNotification(createErrorNotification(new Error('Please select at least one member account to add')));
      return;
    }

    const members = Array.from(selected);
    setBulkAddingMembers(prev => ({ ...prev, [orgAccountId]: true }));

    try {
      let successCount = 0;
      let failCount = 0;

      for (const memberAccountId of members) {
        try {
          // Skip if already onboarded
          if (isMemberOnboarded(memberAccountId)) {
            continue; // Skip already onboarded accounts
          }

          // Get organization details to use role name and external ID
          const org = organizations.find(o => {
            const id = o.organizationManagementAccountId || o.type?.split('#')[1] || '';
            return id === orgAccountId;
          });

          if (!org) {
            failCount++;
            continue;
          }

          // Use organization's role name (remove -org suffix if present) and external ID
          const roleName = org.role_name?.replace('-org', '') || 'tagops-exec-account-role';
          
          await tagopsService.createAccount({
            accountId: memberAccountId,
            accountName: `Org Member ${memberAccountId}`,
            roleName: roleName,
            externalId: org.externalId || generateRandomId(),
          });

          successCount++;
        } catch (error) {
          console.error(`Error adding member account ${memberAccountId}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        showNotification(createSuccessNotification(
          'Member Accounts Added',
          `Successfully added ${successCount} member account${successCount === 1 ? '' : 's'}.${failCount > 0 ? ` ${failCount} account${failCount === 1 ? '' : 's'} failed to add.` : ''}`
        ));
      } else {
        showNotification(createErrorNotification(new Error(`Failed to add ${failCount} member account${failCount === 1 ? '' : 's'}`)));
      }

      // Clear selections
      setSelectedMembers(prev => {
        const newSelected = { ...prev };
        newSelected[orgAccountId] = new Set<string>();
        return newSelected;
      });

      // Refresh accounts to show updated status
      await refreshAccounts();
    } catch (error) {
      showNotification(createErrorNotification(error));
    } finally {
      setBulkAddingMembers(prev => ({ ...prev, [orgAccountId]: false }));
    }
  };

  // Check if a member account is onboarded
  const isMemberOnboarded = (memberAccountId: string): boolean => {
    // First check if account exists in accounts list
    const accountExists = accounts.some(account => account.accountId === memberAccountId);
    if (!accountExists) {
      return false;
    }
    
    // Check if account is in any organization's member list
    const isInOrgMemberList = Object.values(orgMembers).some(members => 
      members.includes(memberAccountId)
    );
    
    // Account is onboarded if it exists in accounts list AND is in an organization's member list
    // OR if it has organizationId/organization_id field (for backward compatibility)
    return isInOrgMemberList || accounts.some(account => 
      account.accountId === memberAccountId && 
      ((account as any).organizationId !== undefined || (account as any).organization_id !== undefined)
    );
  };

  // Check if any organization members are onboarded
  const hasOnboardedMembers = (organizationAccountId: string): boolean => {
    const members = orgMembers[organizationAccountId] || [];
    return members.some(memberAccountId => isMemberOnboarded(memberAccountId));
  };

  // Get onboarded member account
  const getOnboardedMemberAccount = (memberAccountId: string): Account | null => {
    // First try to find account with organizationId field (for backward compatibility)
    let account = accounts.find(account => 
      account.accountId === memberAccountId && 
      ((account as any).organizationId !== undefined || (account as any).organization_id !== undefined)
    );
    
    // If not found, check if account exists and is in any organization's member list
    if (!account) {
      const isInOrgMemberList = Object.values(orgMembers).some(members => 
        members.includes(memberAccountId)
      );
      if (isInOrgMemberList) {
        account = accounts.find(acc => acc.accountId === memberAccountId);
      }
    }
    
    return account ? account : null;
  };

  // Verify member account
  const verifyMemberAccount = async (orgAccountId: string, memberAccountId: string, org: any) => {
    setVerifyingMember(prev => ({ ...prev, [memberAccountId]: true }));
    try {
      // Remove "-org" suffix from organization role_name for member accounts
      const memberRoleName = org.role_name?.endsWith('-org') 
        ? org.role_name.slice(0, -4) 
        : org.role_name;
      
      const response = await tagopsService.verifyAccount(
        memberAccountId,
        memberRoleName,
        org.externalId
      ) as { verified: boolean; message: string; cloudtrail_enabled?: boolean };
      
      if (response && response.verified) {
        setVerifiedMembers(prev => ({ ...prev, [memberAccountId]: true }));
        // Store CloudTrail status
        const cloudtrailEnabled = response.cloudtrail_enabled ?? null;
        setMemberCloudTrailStatus(prev => ({ ...prev, [memberAccountId]: cloudtrailEnabled }));
        
        // Initialize form data for this member
        // Get organization management account ID (fallback to orgAccountId parameter if not found in org object)
        const organizationManagementAccountId = org.organizationManagementAccountId || org.type?.split('#')[1] || orgAccountId;
        setMemberFormData(prev => ({
          ...prev,
          [memberAccountId]: {
            accountId: memberAccountId,
            accountName: `Account ${memberAccountId}`,
            roleName: memberRoleName,
            externalId: org.externalId,
            regions: Array.isArray(org.regions) ? org.regions : (org.regions ? JSON.parse(org.regions) : []),
            organizationId: organizationManagementAccountId,
          }
        }));
        // Show notification with CloudTrail status if applicable
        if (cloudtrailEnabled === false) {
          showNotification(createWarningNotification(
            'Verification Successful with CloudTrail Warning',
            'Account verified successfully, but CloudTrail is not enabled. This may affect event-based tagging features. You can now add it.'
          ));
        } else {
          showNotification(createSuccessNotification('Verification Successful', 'Account verified successfully. You can now add it.'));
        }
      } else {
        setVerifiedMembers(prev => ({ ...prev, [memberAccountId]: false }));
        setMemberCloudTrailStatus(prev => ({ ...prev, [memberAccountId]: null }));
        showNotification(createErrorNotification(new Error('Verification failed')));
      }
    } catch (error) {
      console.error('Member account verification failed:', error);
      showNotification(createErrorNotification(error));
      setVerifiedMembers(prev => ({ ...prev, [memberAccountId]: false }));
      setMemberCloudTrailStatus(prev => ({ ...prev, [memberAccountId]: null }));
    } finally {
      setVerifyingMember(prev => ({ ...prev, [memberAccountId]: false }));
    }
  };

  // Open add member account dialog
  const handleOpenAddMemberDialog = (memberAccountId: string) => {
    if (!verifiedMembers[memberAccountId]) {
      showNotification(createErrorNotification(new Error('Please verify the account before adding')));
      return;
    }
    setCurrentMemberAccountId(memberAccountId);
    setShowAddMemberDialog(true);
  };

  // Add member account
  const handleAddMemberAccount = async () => {
    if (!currentMemberAccountId) return;
    
    if (!verifiedMembers[currentMemberAccountId]) {
      showNotification(createErrorNotification(new Error('Please verify the account before adding')));
      return;
    }

    const formData = memberFormData[currentMemberAccountId];
    if (!formData) {
      showNotification(createErrorNotification(new Error('Account data not found')));
      return;
    }

    if (!formData.accountName || formData.accountName.trim().length === 0) {
      showNotification(createErrorNotification(new Error('Account name is required')));
      return;
    }

    // Check free trial and subscription limits (count ALL accounts including organization accounts)
    const allAccountsCount = accounts.length; // accounts already includes all accounts from /account/list
    if (isExpired) {
      showNotification(createErrorNotification(new Error('Your subscription has expired. Renew your subscription to add AWS accounts.')));
      return;
    }
    if (isFreeTrial && allAccountsCount >= 1) {
      showNotification(createErrorNotification(new Error('Free Trial is limited to 1 AWS Account. Upgrade your subscription to add more accounts.')));
      return;
    }
    if (maxAccounts !== null && allAccountsCount >= maxAccounts) {
      showNotification(createErrorNotification(new Error(`You've reached the maximum of ${maxAccounts} AWS Account${maxAccounts === 1 ? '' : 's'} for your subscription plan.`)));
      return;
    }

    setAddingMemberAccount(prev => ({ ...prev, [currentMemberAccountId]: true }));
    setShowAddMemberDialog(false);
    
    try {
      await tagopsService.createAccount({
        accountName: formData.accountName,
        accountId: formData.accountId,
        roleName: formData.roleName,
        externalId: formData.externalId,
        organizationId: formData.organizationId,
      });
      
      showNotification(createSuccessNotification(
        'Account Added Successfully!',
        `Account "${formData.accountName}" has been added successfully.`
      ));
      
      // Reset verified state for this member
      setVerifiedMembers(prev => {
        const newState = { ...prev };
        delete newState[currentMemberAccountId];
        return newState;
      });
      
      // Refresh accounts list
      await refreshAccounts();
    } catch (error) {
      showNotification(createErrorNotification(error));
    } finally {
      setAddingMemberAccount(prev => ({ ...prev, [currentMemberAccountId]: false }));
      setCurrentMemberAccountId(null);
    }
  };

  // Handle errors from context
  useEffect(() => {
    if (configError?.accounts) {
      showNotification(createErrorNotification(new Error(configError.accounts), refreshAccounts));
    }
  }, [configError?.accounts, showNotification, refreshAccounts]);

  // Set loading state based on context and organizations loading state
  useEffect(() => {
    // Accounts should be considered loading if either:
    // 1. Accounts are loading from context, OR
    // 2. Organizations are still loading (we need org data to filter accounts correctly)
    setLoading(configLoading.accounts || loadingOrganizations);
  }, [configLoading.accounts, loadingOrganizations]);

  // Check subscription status to determine if user is on free trial and get max accounts
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        setLoadingSubscription(true);
        const response = await tagopsService.getSubscriptionStatus();
        
        if (response.subscription) {
          const { isFreeTrial, isExpired, maxAccounts } = getSubscriptionAccountLimits(response.subscription);
          setIsExpired(isExpired);
          setIsFreeTrial(isFreeTrial);
          setMaxAccounts(maxAccounts);
        } else {
          // No subscription = free trial
          setIsFreeTrial(true);
          setIsExpired(false);
          setMaxAccounts(null);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        // On error, assume free trial to be safe
        setIsFreeTrial(true);
        setIsExpired(false);
        setMaxAccounts(null);
      } finally {
        setLoadingSubscription(false);
      }
    };

    checkSubscription();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshAccounts();
      // Force reload members for all organizations
      await loadOrganizations(true);
      showNotification(createSuccessNotification('Accounts Refreshed', 'AWS accounts list has been updated successfully.'));
    } catch (error) {
      showNotification(createErrorNotification(error));
    } finally {
      setRefreshing(false);
    }
  };

  const handleOpenAddDialog = () => {
    const formData = {
      accountName: '',
      accountId: '',
      roleName: 'tagops-exec-account-role',
      externalId: generateRandomId(),
      mainRegion: '',
      regions: [] as string[],
    };
    
    setAccountFormData(formData);
    
    // Set validation errors for empty required fields
    setValidationErrors({
      accountName: validateAccountName(formData.accountName),
      accountId: validateAccountId(formData.accountId),
      roleName: validateRoleName(formData.roleName),
      externalId: validateExternalId(formData.externalId),
    });
    
    // Mark empty required fields as touched to show validation
    setFieldTouched({
      accountName: true, // Mark as touched to show "required" message
      accountId: true,   // Mark as touched to show "required" message
      roleName: false,   // Role name has default value, not touched
      externalId: false, // External ID has generated value, not touched
    });
    
    setVerified(false);
    setCloudtrailEnabled(null);
    setAddingAccount(false);
    setVerifying(false);
    setShowAddDialog(true);
  };

  // Organization handlers
  const handleOpenAddOrgDialog = () => {
    const formData = {
      organizationName: '',
      accountId: '',
      roleName: 'tagops-exec-role',
      externalId: generateRandomId(),
      regions: [] as string[],
      organizationalUnitIds: '',
      includeManagementAccount: 'false',
    };
    
    setOrgFormData(formData);
    
    // Set validation errors for empty required fields
    setOrgValidationErrors({
      organizationName: validateOrganizationName(formData.organizationName),
      accountId: validateAccountId(formData.accountId),
      roleName: validateRoleName(formData.roleName),
      externalId: validateExternalId(formData.externalId),
    });
    
    // Mark empty required fields as touched to show validation
    setOrgFieldTouched({
      organizationName: true,
      accountId: true,
      roleName: false,
      externalId: false,
    });
    
    setVerifiedOrg(false);
    setAddingOrg(false);
    setVerifyingOrg(false);
    setShowAddOrgDialog(true);
  };

  const handleOpenEditOrgDialog = (organization: any) => {
    setCurrentOrg(organization);
    
    const accountId = organization.organizationManagementAccountId || organization.type?.split('#')[1] || '';
    
    // Parse regions from organization data if available
    let regionsList: string[] = [];
    try {
      if (organization.regions) {
        if (Array.isArray(organization.regions)) {
          regionsList = organization.regions;
        } else {
          regionsList = JSON.parse(organization.regions);
        }
      }
    } catch (e) {
      console.error('Failed to parse organization regions', e);
    }
    
    setOrgFormData({
      organizationName: organization.organization_name || '',
      accountId: accountId,
      roleName: organization.role_name || '',
      externalId: organization.externalId || '',
      regions: regionsList,
      organizationalUnitIds: organization.organizationalUnitIds || organization.organizational_unit_ids || '',
      includeManagementAccount: organization.includeManagementAccount || organization.include_management_account || 'false',
    });
    
    // Set validation errors
    setOrgValidationErrors({
      organizationName: validateOrganizationName(organization.organization_name || ''),
      accountId: validateAccountId(accountId),
      roleName: validateRoleName(organization.role_name || ''),
      externalId: validateExternalId(organization.externalId || ''),
    });
    
    setOrgFieldTouched({
      organizationName: true,
      accountId: true,
      roleName: true,
      externalId: true,
    });
    
    setEditVerifiedOrg(false);
    setUpdatingOrg(false);
    setEditVerifyingOrg(false);
    setShowEditOrgDialog(true);
  };

  const verifyOrganization = async () => {
    if (!orgFormData.accountId || !orgFormData.organizationName) {
      showNotification(createErrorNotification(new Error('Please fill in Organization Name and Account ID')));
      return;
    }
    
    const accountIdValue = orgFormData.accountId.trim();
    if (!/^\d{12}$/.test(accountIdValue)) {
      showNotification(createErrorNotification(new Error('AWS Account ID must be 12 digits')));
      return;
    }
    
    setVerifyingOrg(true);
    try {
      const response = await tagopsService.verifyOrganization(
        accountIdValue,
        orgFormData.roleName,
        orgFormData.externalId
      ) as { verified: boolean; message: string };
      
      if (response && response.verified) {
        setVerifiedOrg(true);
        showNotification(createSuccessNotification(
          'Organization Verification Successful',
          response.message || 'Organization verified successfully. You can now add it.'
        ));
      } else {
        setVerifiedOrg(false);
        showNotification(createErrorNotification(
          new Error(response?.message || 'Organization verification failed. Please check your credentials and try again.')
        ));
      }
    } catch (error) {
      console.error('Organization verification failed:', error);
      showNotification(createErrorNotification(error));
      setVerifiedOrg(false);
    } finally {
      setVerifyingOrg(false);
    }
  };

  const verifyEditOrganization = async () => {
    if (!orgFormData.accountId || !orgFormData.organizationName) {
      showNotification(createErrorNotification(new Error('Please fill in Organization Name and Account ID')));
      return;
    }
    
    const accountIdValue = orgFormData.accountId.trim();
    if (!/^\d{12}$/.test(accountIdValue)) {
      showNotification(createErrorNotification(new Error('AWS Account ID must be 12 digits')));
      return;
    }
    
    setEditVerifyingOrg(true);
    try {
      const response = await tagopsService.verifyOrganization(
        accountIdValue,
        orgFormData.roleName,
        orgFormData.externalId
      ) as { verified: boolean; message: string };
      
      if (response && response.verified) {
        setEditVerifiedOrg(true);
        showNotification(createSuccessNotification(
          'Organization Verification Successful',
          response.message || 'Organization verified successfully. You can now update it.'
        ));
      } else {
        setEditVerifiedOrg(false);
        showNotification(createErrorNotification(
          new Error(response?.message || 'Organization verification failed. Please check your credentials and try again.')
        ));
      }
    } catch (error) {
      console.error('Organization verification failed:', error);
      showNotification(createErrorNotification(error));
      setEditVerifiedOrg(false);
    } finally {
      setEditVerifyingOrg(false);
    }
  };

  const handleAddOrganization = async () => {
    if (!verifiedOrg) {
      showNotification(createErrorNotification(new Error('Please verify the organization before adding')));
      return;
    }
    
    const organizationName = orgFormData.organizationName;
    const accountId = orgFormData.accountId;
    
    // Start organization creation - show loading in dialog
    setAddingOrg(true);
    
    try {
      await tagopsService.createOrganization({
        accountId: orgFormData.accountId,
        organizationName: orgFormData.organizationName,
        roleName: orgFormData.roleName,
        externalId: orgFormData.externalId,
        organizationalUnitIds: orgFormData.organizationalUnitIds,
        includeManagementAccount: orgFormData.includeManagementAccount,
      });
      
      // Organization creation successful
      showNotification(createSuccessNotification(
        'Organization Added Successfully!',
        `"${organizationName}" has been added successfully.`
      ));
      
      // Refresh organization members list to present all members
      try {
        const membersResponse = await tagopsService.getOrganizationMembers(accountId);
        setOrgMembers(prev => ({ ...prev, [accountId]: membersResponse.members || [] }));
      } catch (error) {
        console.error(`Error loading members for organization ${accountId}:`, error);
        // Don't show error notification here as organization was created successfully
        // Members will be loaded when organizations are refreshed
        setOrgMembers(prev => ({ ...prev, [accountId]: [] }));
      }
      
      // Close dialog and reset form
      setShowAddOrgDialog(false);
      setVerifiedOrg(false);
      setOrgFormData({
        organizationName: '',
        accountId: '',
        roleName: 'tagops-exec-role',
        externalId: generateRandomId(),
        regions: [],
        organizationalUnitIds: '',
        includeManagementAccount: 'false',
      });
      setOrgValidationErrors({
        organizationName: '',
        accountId: '',
        roleName: '',
        externalId: '',
      });
      setOrgFieldTouched({
        organizationName: false,
        accountId: false,
        roleName: false,
        externalId: false,
      });
      
      // Add to creating organizations to show skeleton
      setCreatingOrganizations(prev => {
        const newMap = new Map(prev).set(accountId, organizationName);
        return newMap;
      });
      
      await loadOrganizations();
      
      // Remove from creating organizations after a delay to ensure skeleton is visible
      setTimeout(() => {
        setCreatingOrganizations(prev => {
          const newMap = new Map(prev);
          newMap.delete(accountId);
          return newMap;
        });
      }, 2000);
    } catch (error) {
      showNotification(createErrorNotification(error));
    } finally {
      setAddingOrg(false);
    }
  };

  const handleEditOrganization = async () => {
    if (!editVerifiedOrg) {
      showNotification(createErrorNotification(new Error('Please verify the organization before updating')));
      return;
    }

    const organizationName = orgFormData.organizationName;
    const accountId = orgFormData.accountId;
    
    setUpdatingOrg(true);
    setShowEditOrgDialog(false);
    setEditVerifiedOrg(false);
    
    try {
      // Update the organization first
      await tagopsService.updateOrganization({
        accountId: orgFormData.accountId,
        organizationName: orgFormData.organizationName,
        roleName: orgFormData.roleName,
        externalId: orgFormData.externalId,
        organizationalUnitIds: orgFormData.organizationalUnitIds,
        includeManagementAccount: orgFormData.includeManagementAccount,
      });
      
      showNotification(createSuccessNotification(
        'Organization Updated Successfully!',
        `Organization "${organizationName}" has been updated successfully.`
      ));
      
      // Refresh organization members list (number of members can change after CloudFormation update)
      let members: string[] = [];
      try {
        const membersResponse = await tagopsService.getOrganizationMembers(accountId);
        members = membersResponse.members || [];
        setOrgMembers(prev => ({ ...prev, [accountId]: members }));
      } catch (error) {
        console.error(`Error refreshing members for organization ${accountId}:`, error);
        showNotification(createErrorNotification(
          new Error(`Failed to refresh organization members: ${error instanceof Error ? error.message : 'Unknown error'}`)
        ));
        // Fall back to cached members if refresh fails
        members = orgMembers[accountId] || [];
      }
      
      // Get organization members and update all onboarded accounts
      const onboardedMemberAccounts = members
        .map(memberAccountId => {
          const account = getOnboardedMemberAccount(memberAccountId);
          return account ? { accountId: memberAccountId, account } : null;
        })
        .filter((item): item is { accountId: string; account: Account } => item !== null);
      
      if (onboardedMemberAccounts.length > 0) {
        // Remove -org suffix from role_name for member accounts
        const memberRoleName = orgFormData.roleName.endsWith('-org') 
          ? orgFormData.roleName.slice(0, -4) 
          : orgFormData.roleName;
        
        let updatedCount = 0;
        let failedCount = 0;
        
        // Update each onboarded account
        for (const { accountId: memberAccountId, account } of onboardedMemberAccounts) {
          // Add to updating accounts to show loading state
          setUpdatingAccounts(prev => {
            const newMap = new Map(prev);
            newMap.set(memberAccountId, account.account_name);
            return newMap;
          });
          
          try {
            // Show notification that we're updating this account
            showNotification(createSuccessNotification(
              'Updating Account',
              `Updating account "${account.account_name}" (${memberAccountId})...`
            ));
            
            // Update the account with organization's role_name and external_id
            await tagopsService.updateAccount({
              accountId: memberAccountId,
              accountName: account.account_name,
              roleName: memberRoleName,
              externalId: orgFormData.externalId,
            });
            
            updatedCount++;
            showNotification(createSuccessNotification(
              'Account Updated',
              `Account "${account.account_name}" (${memberAccountId}) has been updated with new organization credentials.`
            ));
          } catch (error: any) {
            failedCount++;
            showNotification(createErrorNotification(
              new Error(`Failed to update account "${account.account_name}" (${memberAccountId}): ${error?.message || 'Unknown error'}`)
            ));
          } finally {
            // Remove from updating accounts after update completes
            setUpdatingAccounts(prev => {
              const newMap = new Map(prev);
              newMap.delete(memberAccountId);
              return newMap;
            });
          }
        }
        
        // Show summary notification
        if (updatedCount > 0) {
          showNotification(createSuccessNotification(
            'Member Accounts Updated',
            `Successfully updated ${updatedCount} member account${updatedCount === 1 ? '' : 's'}.${failedCount > 0 ? ` ${failedCount} account${failedCount === 1 ? '' : 's'} failed to update.` : ''}`
          ));
        }
      }
      
      await loadOrganizations();
      await refreshAccounts(); // Refresh accounts to show updated information
    } catch (error) {
      showNotification(createErrorNotification(error));
    } finally {
      setUpdatingOrg(false);
    }
  };

  const handleDeleteOrganization = (organization: any) => {
    setOrgToDelete(organization);
    setShowDeleteOrgDialog(true);
  };

  const confirmDeleteOrganization = async () => {
    if (!orgToDelete) return;

    const accountId = orgToDelete.organizationManagementAccountId || orgToDelete.type?.split('#')[1] || '';
    const organizationName = orgToDelete.organization_name || 'Unknown';
    
    // Store the deleted organization for CloudFormation dialog
    setDeletedOrganization(orgToDelete);
    
    // Start organization deletion - show loading in dialog
    setDeletingOrg(true);
    
    try {
      await tagopsService.deleteOrganization({ accountId });
      
      // Organization deletion successful
      showNotification(createSuccessNotification(
        'Organization Deleted Successfully!',
        `"${organizationName}" has been deleted successfully.`
      ));
      
      // Close dialog
      setShowDeleteOrgDialog(false);
      setOrgToDelete(null);
      
      // Show CloudFormation cleanup dialog if organization has CloudFormation info
      if (orgToDelete.cloudformation_arn && orgToDelete.onboarding_region) {
        setShowCloudFormationOrgDialog(true);
      }
      
      // Add to deleting organizations to show skeleton
      setDeletingOrganizations(prev => {
        const newMap = new Map(prev).set(accountId, organizationName);
        return newMap;
      });
      
      await loadOrganizations();
      setOrgMembers(prev => {
        const newMembers = { ...prev };
        delete newMembers[accountId];
        return newMembers;
      });
      
      // Remove from deleting organizations after a delay to ensure skeleton is visible
      setTimeout(() => {
        setDeletingOrganizations(prev => {
          const newMap = new Map(prev);
          newMap.delete(accountId);
          return newMap;
        });
      }, 2000);
    } catch (error) {
      showNotification(createErrorNotification(error));
      // Remove from deleting organizations on error
      setDeletingOrganizations(prev => {
        const newMap = new Map(prev);
        newMap.delete(accountId);
        return newMap;
      });
    } finally {
      setDeletingOrg(false);
    }
  };

  // Organization CloudFormation functions
  const openOrgCloudFormationStack = () => {
    const baseUrl = 'https://console.aws.amazon.com/cloudformation/home#/stacks/quickcreate';
    const templateUrl = process.env.REACT_APP_ORG_CLOUDFORMATION_TEMPLATE_URL;
    if (!templateUrl) {
      showNotification(createErrorNotification(new Error('CloudFormation template URL not set')));
      return;
    }

    const params = new URLSearchParams({
      templateURL: templateUrl,
      stackName: 'cf-tagops-org-onboarding',
      param_RoleName: orgFormData.roleName,
      param_ExternalId: orgFormData.externalId
    });
    
    // Add regions parameter if regions are selected
    if (orgFormData.regions && orgFormData.regions.length > 0) {
      // For CommaDelimitedList parameters, AWS CloudFormation expects comma-separated values
      params.append('param_AWSAdditionalRegions', orgFormData.regions.join(', '));
    }
    
    // Add Organizational Unit IDs parameter if provided
    if (orgFormData.organizationalUnitIds && orgFormData.organizationalUnitIds.trim()) {
      params.append('param_OrganizationalUnitIds', orgFormData.organizationalUnitIds.trim());
    }
    
    // Add Include Management Account parameter
    params.append('param_IncludeManagementAccount', orgFormData.includeManagementAccount);
    
    window.open(`${baseUrl}?${params.toString()}`, '_blank');
  };

  const copyOrgCloudFormationLink = () => {
    const baseUrl = 'https://console.aws.amazon.com/cloudformation/home#/stacks/quickcreate';
    const templateUrl = process.env.REACT_APP_ORG_CLOUDFORMATION_TEMPLATE_URL;
    if (!templateUrl) {
      showNotification(createErrorNotification(new Error('CloudFormation template URL not set')));
      return;
    }

    const params = new URLSearchParams({
      templateURL: templateUrl,
      stackName: 'cf-tagops-org-onboarding',
      param_RoleName: orgFormData.roleName,
      param_ExternalId: orgFormData.externalId
    });
    
    // Add regions parameter if regions are selected
    if (orgFormData.regions && orgFormData.regions.length > 0) {
      // For CommaDelimitedList parameters, AWS CloudFormation expects comma-separated values
      params.append('param_AWSAdditionalRegions', orgFormData.regions.join(', '));
    }
    
    // Add Organizational Unit IDs parameter if provided
    if (orgFormData.organizationalUnitIds && orgFormData.organizationalUnitIds.trim()) {
      params.append('param_OrganizationalUnitIds', orgFormData.organizationalUnitIds.trim());
    }
    
    // Add Include Management Account parameter
    params.append('param_IncludeManagementAccount', orgFormData.includeManagementAccount);
    
    const link = `${baseUrl}?${params.toString()}`;
    copyToClipboard(link, 'CloudFormation link');
  };

  const openUpdateOrgCloudFormationStack = () => {
    if (!currentOrg || !currentOrg.cloudformation_arn || !currentOrg.onboarding_region) {
      showNotification(createErrorNotification(new Error('CloudFormation stack information not available')));
      return;
    }

    const onboardingRegion = currentOrg.onboarding_region;
    const cloudformationArn = currentOrg.cloudformation_arn;
    
    const updateUrl = `https://${onboardingRegion}.console.aws.amazon.com/cloudformation/home?region=${onboardingRegion}#/stacks/update?stackId=${encodeURIComponent(cloudformationArn)}`;
    
    window.open(updateUrl, '_blank');
  };

  const copyUpdateOrgCloudFormationLink = () => {
    if (!currentOrg || !currentOrg.cloudformation_arn || !currentOrg.onboarding_region) {
      showNotification(createErrorNotification(new Error('CloudFormation stack information not available')));
      return;
    }

    const onboardingRegion = currentOrg.onboarding_region;
    const cloudformationArn = currentOrg.cloudformation_arn;
    
    const updateUrl = `https://${onboardingRegion}.console.aws.amazon.com/cloudformation/home?region=${onboardingRegion}#/stacks/update?stackId=${encodeURIComponent(cloudformationArn)}`;
    
    copyToClipboard(updateUrl, 'CloudFormation update link');
  };
  
  const handleGenerateExternalId = () => {
    setAccountFormData({
      ...accountFormData,
      externalId: generateRandomId()
    });
  };
  
  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    showNotification(createSuccessNotification('Copied', `${type} copied to clipboard`));
  };
  
  const verifyAccount = async () => {
    if (!accountFormData.accountId || !accountFormData.accountName) {
      showNotification(createErrorNotification(new Error('Please fill in Account Name and Account ID')));
      return;
    }
    
    // Ensure accountId is the numeric AWS Account ID, not the account name
    const accountIdValue = accountFormData.accountId.trim();
    if (!/^\d{12}$/.test(accountIdValue)) {
      showNotification(createErrorNotification(new Error('AWS Account ID must be 12 digits')));
      return;
    }
    
    setVerifying(true);
    try {
      const response = await tagopsService.verifyAccount(
        accountIdValue, // Using the validated account ID
        accountFormData.roleName,
        accountFormData.externalId
      ) as { verified: boolean; message: string; cloudtrail_enabled?: boolean };
      
      if (response && response.verified) {
        setVerified(true);
        setCloudtrailEnabled(response.cloudtrail_enabled ?? null);
        
        // No notification for verification - user can see the result in the UI
      }
    } catch (error) {
      console.error('Account verification failed:', error);
      showNotification(createErrorNotification(error));
      setVerified(false);
      setCloudtrailEnabled(null);
    } finally {
      setVerifying(false);
    }
  };
  
  const openCloudFormationStack = () => {
    const mainRegion = accountFormData.mainRegion || 'us-east-1';
    const baseUrl = `https://${mainRegion}.console.aws.amazon.com/cloudformation/home?region=${mainRegion}#/stacks/quickcreate`;
    const templateUrl = process.env.REACT_APP_CLOUDFORMATION_TEMPLATE_URL || 
      'https://s3-test-tagops-public-741570836596-eu-west-1.s3.eu-west-1.amazonaws.com/customer_onboarding_cf.yaml';
    
    const params = new URLSearchParams({
      templateURL: templateUrl,
      stackName: 'cf-tagops-onboarding',
      param_RoleName: accountFormData.roleName,
      param_ExternalId: accountFormData.externalId
    });
    
    // Add additional regions parameter (main region is where the stack is deployed)
    if (accountFormData.regions && accountFormData.regions.length > 0) {
      params.append('param_AWSAdditionalRegions', accountFormData.regions.join(', '));
    }
    
    window.open(`${baseUrl}?${params.toString()}`, '_blank');
  };

  const copyCloudFormationLink = () => {
    const mainRegion = accountFormData.mainRegion || 'us-east-1';
    const baseUrl = `https://${mainRegion}.console.aws.amazon.com/cloudformation/home?region=${mainRegion}#/stacks/quickcreate`;
    const templateUrl = process.env.REACT_APP_CLOUDFORMATION_TEMPLATE_URL || 
      'https://s3-test-tagops-public-741570836596-eu-west-1.s3.eu-west-1.amazonaws.com/customer_onboarding_cf.yaml';
    
    const params = new URLSearchParams({
      templateURL: templateUrl,
      stackName: 'cf-tagops-onboarding',
      param_RoleName: accountFormData.roleName,
      param_ExternalId: accountFormData.externalId
    });
    
    if (accountFormData.regions && accountFormData.regions.length > 0) {
      params.append('param_AWSAdditionalRegions', accountFormData.regions.join(', '));
    }
    
    const link = `${baseUrl}?${params.toString()}`;
    copyToClipboard(link, 'CloudFormation link');
  };

  const handleOpenEditDialog = (account: Account) => {
    setCurrentAccount(account);
    
    const mainRegion = account.onboarding_region || '';
    
    // Parse regions from account data (these are all monitored regions; additional = all minus main)
    let regionsList: string[] = [];
    try {
      if (account.regions) {
        if (Array.isArray(account.regions)) {
          regionsList = account.regions;
        } else {
          regionsList = JSON.parse(account.regions);
        }
      }
    } catch (e) {
      console.error('Failed to parse account regions', e);
    }
    const additionalRegions = mainRegion ? regionsList.filter((r) => r !== mainRegion) : regionsList;
    
    setAccountFormData({
      accountName: account.account_name,
      accountId: account.accountId,
      roleName: account.role_name,
      externalId: account.externalId,
      mainRegion,
      regions: additionalRegions,
    });
    // Reset validation errors for edit dialog
    setValidationErrors({
      accountName: validateAccountName(account.account_name),
      accountId: validateAccountId(account.accountId),
      roleName: validateRoleName(account.role_name),
      externalId: validateExternalId(account.externalId),
    });
    setFieldTouched({
      accountName: true,
      accountId: true,
      roleName: true,
      externalId: true,
    });
    setEditVerified(false);
    setEditCloudtrailEnabled(null);
    setEditVerifying(false);
    setUpdatingAccount(false);
    setShowEditDialog(true);
  };

  const handleAddAccount = async () => {
    if (!verified) {
      showNotification(createErrorNotification(new Error('Please verify the account before adding')));
      return;
    }
    
    const accountName = accountFormData.accountName;
    const accountId = accountFormData.accountId;
    
    // Add to creating accounts immediately to show skeleton
    setCreatingAccounts(prev => {
      const newMap = new Map(prev).set(accountId, accountName);
      return newMap;
    });
    
    // Close dialog immediately and show processing notification
    setShowAddDialog(false);
    setVerified(false);
    
    // Show immediate notification that account creation has started
    showNotification(createSuccessNotification(
      'Account Creation Started',
      `Account Creation Started`
    ));
    
    // Add to notification bell for later access
    if ((window as any).addAccountNotification) {
      (window as any).addAccountNotification(
        'Account Creation Started',
        `While creating your account, please modify the services you would like to scan.`,
        accountName,
        'info'
      );
    }
    
    // Start account creation in background
    setAddingAccount(true);
    
    try {
      await tagopsService.createAccount({
        accountName: accountFormData.accountName,
        accountId: accountFormData.accountId,
        roleName: accountFormData.roleName,
        externalId: accountFormData.externalId,
      });
      
      // Account creation successful - show notification based on CloudTrail status
      if (cloudtrailEnabled === true) {
        // CloudTrail is enabled - show success notification
        showNotification(createSuccessNotification(
          'Account Added Successfully!',
          `"${accountName}" has been added successfully. Configure services to scan.`,
          [
            {
              label: 'Configure Services',
              action: () => navigate('/settings?tab=account-settings'),
              color: 'primary' as const
            }
          ]
        ));
        
        // Update notification bell
        if ((window as any).addAccountNotification) {
          (window as any).addAccountNotification(
            'Account Added Successfully!',
            `"${accountName}" has been added successfully. Configure services to scan.`,
            accountName,
            'success'
          );
        }
      } else if (cloudtrailEnabled === false) {
        // CloudTrail is disabled - show warning notification
        showNotification(createWarningNotification(
          'Account Added with CloudTrail Warning',
          `"${accountName}" added successfully, but CloudTrail is not enabled. This may affect event-based tagging features.`,
          [
            {
              label: 'Configure Services',
              action: () => navigate('/settings?tab=account-settings'),
              color: 'primary' as const
            }
          ]
        ));
        
        // Update notification bell with warning
        if ((window as any).addAccountNotification) {
          (window as any).addAccountNotification(
            'Account Added with CloudTrail Warning',
            `"${accountName}" added successfully, but CloudTrail is not enabled. This may affect event-based tagging features.`,
            accountName,
            'warning'
          );
        }
      } else {
        // CloudTrail status unknown - show neutral notification
        showNotification(createSuccessNotification(
          'Account Added Successfully!',
          `"${accountName}" has been added successfully. Configure services to scan.`,
          [
            {
              label: 'Configure Services',
              action: () => navigate('/settings?tab=account-settings'),
              color: 'primary' as const
            }
          ]
        ));
        
        // Update notification bell
        if ((window as any).addAccountNotification) {
          (window as any).addAccountNotification(
            'Account Added Successfully!',
            `"${accountName}" has been added successfully. Configure services to scan.`,
            accountName,
            'success'
          );
        }
      }
      
      // Add a longer delay to ensure skeleton is visible, then refresh accounts
      setTimeout(() => {
        refreshAccounts();
      }, 3000);
    } catch (error) {
      // Account creation failed - show error notification
      showNotification(createErrorNotification(error));
      
      // Update notification bell with error
      if ((window as any).addAccountNotification) {
        (window as any).addAccountNotification(
          'Account Creation Failed',
          `Failed to create "${accountName}". Please try again.`,
          accountName,
          'error'
        );
      }
    } finally {
      setAddingAccount(false);
      // Remove skeleton after a delay to ensure it's visible
      setTimeout(() => {
        setCreatingAccounts(prev => {
          const newMap = new Map(prev);
          newMap.delete(accountId);
          return newMap;
        });
      }, 4000);
    }
  };

  const handleEditAccount = async () => {
    if (!editVerified) {
      showNotification(createErrorNotification(new Error('Please verify the account before updating')));
      return;
    }

    const accountName = accountFormData.accountName;
    const accountId = accountFormData.accountId;
    
    // Add to updating accounts immediately to show skeleton
    setUpdatingAccounts(prev => {
      const newMap = new Map(prev).set(accountId, accountName);
      return newMap;
    });
    
    // Close dialog immediately
    setShowEditDialog(false);
    setEditVerified(false);
    
    // Start account update in background
    setUpdatingAccount(true);
    
    try {
      await tagopsService.updateAccount({
        accountName: accountFormData.accountName,
        accountId: accountFormData.accountId,
        roleName: accountFormData.roleName,
        externalId: accountFormData.externalId,
      });
      
      // Account update successful - show notification based on CloudTrail status
      if (editCloudtrailEnabled === true) {
        // CloudTrail is enabled - show success notification
        showNotification(createSuccessNotification(
          'Account Updated Successfully!',
          `AWS account "${accountName}" has been updated successfully.`
        ));
      } else if (editCloudtrailEnabled === false) {
        // CloudTrail is disabled - show warning notification
        showNotification(createWarningNotification(
          'Account Updated with CloudTrail Warning',
          `AWS account "${accountName}" has been updated successfully, but CloudTrail is not enabled. This may affect event-based tagging features.`
        ));
      } else {
        // CloudTrail status unknown - show neutral notification
        showNotification(createSuccessNotification(
          'Account Updated Successfully!',
          `AWS account "${accountName}" has been updated successfully.`
        ));
      }
      
      // Add a delay to ensure skeleton is visible, then refresh accounts
      setTimeout(() => {
        refreshAccounts();
      }, 2000);
    } catch (error) {
      // Account update failed - show error notification
      showNotification(createErrorNotification(error));
    } finally {
      setUpdatingAccount(false);
      // Remove skeleton after a delay to ensure it's visible
      setTimeout(() => {
        setUpdatingAccounts(prev => {
          const newMap = new Map(prev);
          newMap.delete(accountId);
          return newMap;
        });
      }, 3000);
    }
  };

  const handleDeleteAccount = (account: Account) => {
    setAccountToDelete(account);
    setShowDeleteDialog(true);
  };

  const confirmDeleteAccount = async () => {
    if (!accountToDelete) return;

    const accountId = accountToDelete.accountId;
    const accountName = accountToDelete.account_name;
    
    // Check if account is from an organization
    const isOrganizationAccount = (accountToDelete as any).organizationId !== undefined || 
                                  (accountToDelete as any).organization_id !== undefined;
    
    // Close dialog immediately and show processing notification
    setShowDeleteDialog(false);
    
    // No immediate notification for deletion start - user can see CloudFormation dialog
    
    // Store the deleted account for CloudFormation dialog immediately
    setDeletedAccount(accountToDelete);
    setAccountToDelete(null);
    
    // Show CloudFormation cleanup dialog only if:
    // 1. Account has CloudFormation info
    // 2. Account is NOT from an organization
    if (accountToDelete.cloudformation_arn && accountToDelete.onboarding_region && !isOrganizationAccount) {
      setShowCloudFormationDialog(true);
    }
    
    // Start account deletion in background
    setDeletingAccount(true);
    setDeletingAccounts(prev => new Map(prev).set(accountId, accountName));
    
    try {
      await tagopsService.deleteAccount({ accountId: accountToDelete.accountId });
      
      // Account deletion successful - update notification
      showNotification(createSuccessNotification(
        'Account Deleted Successfully!',
        `"${accountName}" has been deleted successfully.`
      ));
      
      // Update notification bell
      if ((window as any).addAccountNotification) {
        (window as any).addAccountNotification(
          'Account Deleted Successfully!',
          `"${accountName}" has been deleted successfully.`,
          accountName,
          'success'
        );
      }
      
      refreshAccounts();
    } catch (error) {
      // Account deletion failed - show error notification
      showNotification(createErrorNotification(error));
      
      // Update notification bell with error
      if ((window as any).addAccountNotification) {
        (window as any).addAccountNotification(
          'Account Deletion Failed',
          `Failed to delete "${accountName}". Please try again.`,
          accountName,
          'error'
        );
      }
    } finally {
      setDeletingAccount(false);
      setDeletingAccounts(prev => {
        const newMap = new Map(prev);
        newMap.delete(accountId);
        return newMap;
      });
    }
  };


  const openCloudFormationCleanup = () => {
    if (!deletedAccount || !deletedAccount.cloudformation_arn || !deletedAccount.onboarding_region) {
      showNotification(createErrorNotification(new Error('CloudFormation stack information not available')));
      return;
    }

    const onboardingRegion = deletedAccount.onboarding_region;
    const cloudformationArn = deletedAccount.cloudformation_arn;
    
    const cleanupUrl = `https://${onboardingRegion}.console.aws.amazon.com/cloudformation/home?region=${onboardingRegion}#/stacks?filteringText=&filteringStatus=active&viewNested=false&stackId=${encodeURIComponent(cloudformationArn)}`;
    
    window.open(cleanupUrl, '_blank');
    setShowCloudFormationDialog(false);
    setDeletedAccount(null);
  };

  const copyCloudFormationCleanupLink = () => {
    if (!deletedAccount || !deletedAccount.cloudformation_arn || !deletedAccount.onboarding_region) {
      showNotification(createErrorNotification(new Error('CloudFormation stack information not available')));
      return;
    }

    const onboardingRegion = deletedAccount.onboarding_region;
    const cloudformationArn = deletedAccount.cloudformation_arn;
    
    const cleanupUrl = `https://${onboardingRegion}.console.aws.amazon.com/cloudformation/home?region=${onboardingRegion}#/stacks?filteringText=&filteringStatus=active&viewNested=false&stackId=${encodeURIComponent(cloudformationArn)}`;
    
    copyToClipboard(cleanupUrl, 'CloudFormation cleanup link');
  };

  const dismissCloudFormationDialog = () => {
    setShowCloudFormationDialog(false);
    setDeletedAccount(null);
  };

  const openCloudFormationCleanupOrg = () => {
    if (!deletedOrganization || !deletedOrganization.cloudformation_arn || !deletedOrganization.onboarding_region) {
      showNotification(createErrorNotification(new Error('CloudFormation stack information not available')));
      return;
    }

    const onboardingRegion = deletedOrganization.onboarding_region;
    const cloudformationArn = deletedOrganization.cloudformation_arn;
    
    const cleanupUrl = `https://${onboardingRegion}.console.aws.amazon.com/cloudformation/home?region=${onboardingRegion}#/stacks?filteringText=&filteringStatus=active&viewNested=false&stackId=${encodeURIComponent(cloudformationArn)}`;
    
    window.open(cleanupUrl, '_blank');
    setShowCloudFormationOrgDialog(false);
    setDeletedOrganization(null);
  };

  const copyCloudFormationCleanupLinkOrg = () => {
    if (!deletedOrganization || !deletedOrganization.cloudformation_arn || !deletedOrganization.onboarding_region) {
      showNotification(createErrorNotification(new Error('CloudFormation stack information not available')));
      return;
    }

    const onboardingRegion = deletedOrganization.onboarding_region;
    const cloudformationArn = deletedOrganization.cloudformation_arn;
    
    const cleanupUrl = `https://${onboardingRegion}.console.aws.amazon.com/cloudformation/home?region=${onboardingRegion}#/stacks?filteringText=&filteringStatus=active&viewNested=false&stackId=${encodeURIComponent(cloudformationArn)}`;
    
    copyToClipboard(cleanupUrl, 'CloudFormation cleanup link');
  };

  const dismissCloudFormationDialogOrg = () => {
    setShowCloudFormationOrgDialog(false);
    setDeletedOrganization(null);
  };

  const verifyEditAccount = async () => {
    if (!accountFormData.accountId || !accountFormData.accountName) {
      showNotification(createErrorNotification(new Error('Please fill in Account Name and Account ID')));
      return;
    }
    
    // Ensure accountId is the numeric AWS Account ID, not the account name
    const accountIdValue = accountFormData.accountId.trim();
    if (!/^\d{12}$/.test(accountIdValue)) {
      showNotification(createErrorNotification(new Error('AWS Account ID must be 12 digits')));
      return;
    }
    
    setEditVerifying(true);
    try {
      const response = await tagopsService.verifyAccount(
        accountIdValue, // Using the validated account ID
        accountFormData.roleName,
        accountFormData.externalId
      ) as { verified: boolean; message: string; cloudtrail_enabled?: boolean };
      
      if (response && response.verified) {
        setEditVerified(true);
        setEditCloudtrailEnabled(response.cloudtrail_enabled ?? null);
        
        // No notification for verification - user can see the result in the UI
      }
    } catch (error) {
      console.error('Account verification failed:', error);
      showNotification(createErrorNotification(error));
      setEditVerified(false);
      setEditCloudtrailEnabled(null);
    } finally {
      setEditVerifying(false);
    }
  };

  const openUpdateCloudFormationStack = () => {
    if (!currentAccount || !currentAccount.cloudformation_arn || !currentAccount.onboarding_region) {
      showNotification(createErrorNotification(new Error('CloudFormation stack information not available')));
      return;
    }

    const onboardingRegion = currentAccount.onboarding_region;
    const cloudformationArn = currentAccount.cloudformation_arn;
    
    const updateUrl = `https://${onboardingRegion}.console.aws.amazon.com/cloudformation/home?region=${onboardingRegion}#/stacks/update?stackId=${encodeURIComponent(cloudformationArn)}`;
    
    window.open(updateUrl, '_blank');
  };

  const copyUpdateCloudFormationLink = () => {
    if (!currentAccount || !currentAccount.cloudformation_arn || !currentAccount.onboarding_region) {
      showNotification(createErrorNotification(new Error('CloudFormation stack information not available')));
      return;
    }

    const onboardingRegion = currentAccount.onboarding_region;
    const cloudformationArn = currentAccount.cloudformation_arn;
    
    const updateUrl = `https://${onboardingRegion}.console.aws.amazon.com/cloudformation/home?region=${onboardingRegion}#/stacks/update?stackId=${encodeURIComponent(cloudformationArn)}`;
    

    copyToClipboard(updateUrl, 'CloudFormation update link');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'inactive':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <CheckCircleIcon color="success" />;
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
          AWS Accounts
        </Typography>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">
                Connected AWS Accounts
              </Typography>
              <Skeleton variant="rectangular" width={180} height={40} />
            </Box>

            {/* Table Skeleton */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Account ID</TableCell>
                    <TableCell>Regions</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton variant="text" width="80%" height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="60%" height={20} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
                          <Skeleton variant="rectangular" width={50} height={24} sx={{ borderRadius: 1 }} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
                          <Skeleton variant="text" width="60%" height={20} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
                          <Skeleton variant="rectangular" width={70} height={32} sx={{ borderRadius: 1 }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Check if us-east-1 is onboarded - derive from actual account data (source of truth)
  // Previously used serviceConfig.onboarded_regions, but that backend returns regions from
  // only the first DynamoDB item, causing false warnings when us-east-1 is in other accounts
  const allOnboardedRegions = new Set<string>();
  for (const account of accounts) {
    let regionsList: string[] = [];
    try {
      if (account.regions) {
        regionsList = Array.isArray(account.regions) ? account.regions : JSON.parse(account.regions as string);
      }
      const allowedField = (account as any).allowed_regions || (account as any).allowerd_regions;
      if (allowedField && regionsList.length === 0) {
        regionsList = Array.isArray(allowedField) ? allowedField : JSON.parse(allowedField);
      }
    } catch {
      // ignore parse errors
    }
    regionsList.forEach((r: string) => r && allOnboardedRegions.add(r.toLowerCase()));
  }
  const isUsEast1Onboarded = allOnboardedRegions.size === 0 ? true : allOnboardedRegions.has('us-east-1');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          AWS Accounts
        </Typography>
        {!isUsEast1Onboarded && accounts.length > 0 && (
          <Tooltip
            title="us-east-1 is not onboarded. Some monitoring and event-based features may be impacted until us-east-1 is onboarded."
            arrow
          >
            <WarningIcon color="warning" sx={{ fontSize: 26 }} />
          </Tooltip>
        )}
      </Box>

      {!isUsEast1Onboarded && accounts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            us-east-1 is not onboarded
          </Typography>
          <Typography variant="body2">
            TagOps recommends onboarding <strong>us-east-1</strong>. Disabling or not onboarding this region can affect monitoring and event-based features.
          </Typography>
        </Alert>
      )}

      {/* Connected Accounts */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Connected AWS Accounts
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
              <Tooltip
                title={
                  isExpired
                    ? "Your subscription has expired. Renew your subscription to add AWS accounts."
                    : isFreeTrial && accounts.length >= 1
                    ? "Free Trial is limited to 1 AWS Account. Upgrade your subscription to add more accounts."
                    : maxAccounts !== null && accounts.length >= maxAccounts
                    ? `You've reached the maximum of ${maxAccounts} AWS Account${maxAccounts === 1 ? '' : 's'} for your subscription plan.`
                    : ""
                }
                arrow
              >
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <PermissionButton
                    permission="PowerUser"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAddDialog}
                    disabled={
                      isExpired ||
                      (isFreeTrial && accounts.length >= 1) || 
                      (maxAccounts !== null && accounts.length >= maxAccounts)
                    }
                  >
                    Add AWS Account
                  </PermissionButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  isExpired
                    ? "Your subscription has expired. Renew your subscription to add AWS organizations."
                    : isFreeTrial && accounts.length >= 1
                    ? "Free Trial is limited to 1 AWS Account. Upgrade your subscription to add more accounts."
                    : maxAccounts !== null && accounts.length >= maxAccounts
                    ? `You've reached the maximum of ${maxAccounts} AWS Account${maxAccounts === 1 ? '' : 's'} for your subscription plan.`
                    : ""
                }
                arrow
              >
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <PermissionButton
                    permission="PowerUser"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAddOrgDialog}
                    disabled={
                      isExpired ||
                      (isFreeTrial && accounts.length >= 1) || 
                      (maxAccounts !== null && accounts.length >= maxAccounts)
                    }
                  >
                    Add AWS Organization
                  </PermissionButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Free Trial Limitation Alert */}
          {isFreeTrial && accounts.length >= 1 && (
            <Alert 
              severity="warning" 
              sx={{ mb: 3 }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => navigate('/settings?tab=subscription')}
                >
                  Upgrade
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Free Trial Account Limit Reached
              </Typography>
              <Typography variant="body2">
                You've reached the maximum of 1 AWS Account for Free Trial users. Upgrade your subscription to connect multiple AWS accounts.
              </Typography>
            </Alert>
          )}

          {/* Subscription Expired Alert */}
          {isExpired && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => navigate('/settings?tab=subscription')}
                >
                  Renew Subscription
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Subscription Expired
              </Typography>
              <Typography variant="body2">
                Your subscription has expired. You cannot add new AWS accounts. Renew your subscription to restore full access and continue adding accounts.
              </Typography>
            </Alert>
          )}

          {/* Subscription Account Limit Reached Alert */}
          {!isFreeTrial && !isExpired && maxAccounts !== null && accounts.length >= maxAccounts && (
            <Alert 
              severity="warning" 
              sx={{ mb: 3 }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => navigate('/settings?tab=subscription')}
                >
                  Manage Subscription
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Account Limit Reached
              </Typography>
              <Typography variant="body2">
                You've reached the maximum of {maxAccounts} AWS Account{maxAccounts === 1 ? '' : 's'} for your subscription plan. 
                {maxAccounts === 1 
                  ? ' Upgrade your subscription to connect multiple AWS accounts.'
                  : ' Upgrade your subscription or remove an existing account to add more accounts.'}
              </Typography>
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : accounts.length === 0 && creatingAccounts.size === 0 && updatingAccounts.size === 0 ? (
            <Alert severity="info">
              No AWS accounts connected yet. Click the "Add AWS Account" button to add your first account.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Account ID</TableCell>
                    <TableCell>Regions</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Show creating accounts */}
                  {Array.from(creatingAccounts.entries()).map(([accountId, accountName]) => (
                    <TableRow key={`creating-${accountId}`} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {accountName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {accountId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Processing...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Creating
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Please wait...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Show updating accounts (exclude organization accounts) */}
                  {Array.from(updatingAccounts.entries())
                    .filter(([accountId]) => {
                      // Exclude organization accounts from main table
                      const account = accounts.find(acc => acc.accountId === accountId);
                      return !account || (!(account as any).organizationId && !(account as any).organization_id);
                    })
                    .map(([accountId, accountName]) => (
                    <TableRow key={`updating-${accountId}`} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {accountName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {accountId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Processing...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Updating
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Please wait...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Show deleting accounts (exclude organization accounts) */}
                  {Array.from(deletingAccounts.entries())
                    .filter(([accountId]) => {
                      // Exclude organization accounts from main table
                      const account = accounts.find(acc => acc.accountId === accountId);
                      return !account || (!(account as any).organizationId && !(account as any).organization_id);
                    })
                    .map(([accountId, accountName]) => (
                    <TableRow key={`deleting-${accountId}`} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {accountName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {accountId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Processing...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Deleting
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Please wait...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Show existing accounts (exclude organization onboarded accounts) */}
                  {accounts.filter(account => {
                    // Exclude accounts that are part of any organization
                    const isOrgMember = Object.values(orgMembers).some(members => 
                      members.includes(account.accountId)
                    );
                    return !deletingAccounts.has(account.accountId) && 
                           !updatingAccounts.has(account.accountId) &&
                           !(account as any).organizationId &&
                           !(account as any).organization_id &&
                           !isOrgMember;
                  }).map((account) => {
                    // Parse regions - handle both array and JSON string formats
                    let regionsList: string[] = [];
                    let allowedRegionsList: string[] = [];
                    try {
                      if (account.regions) {
                        // Check if regions is already an array or needs parsing
                        if (Array.isArray(account.regions)) {
                          regionsList = account.regions;
                        } else {
                          regionsList = JSON.parse(account.regions);
                        }
                      }

                      // Parse allowed regions - handle both correct field and typo, plus array/JSON string formats
                      const allowedField: any = (account as any).allowed_regions || (account as any).allowerd_regions;
                      if (allowedField) {
                        if (Array.isArray(allowedField)) {
                          allowedRegionsList = allowedField;
                        } else if (typeof allowedField === 'string') {
                          allowedRegionsList = JSON.parse(allowedField);
                        }
                      }
                    } catch (e) {
                      console.error('Failed to parse regions', e);
                    }

                    const isUsEast1EnabledForAccount =
                      allowedRegionsList.length > 0 ? allowedRegionsList.includes('us-east-1') : true; // if we don't know allowed regions, don't warn
                    
                    return (
                      <TableRow key={account.accountId} hover>
                      <TableCell>
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: 'primary.main'
                              }
                            }}
                            onClick={() => {
                              setSelectedAccountForDetails(account);
                              setShowAccountDetailsDialog(true);
                            }}
                          >
                              {account.account_name}
                          </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {account.accountId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {regionsList.map((region) => (
                              <Chip
                                key={region}
                                label={region}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                            {regionsList.length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                None
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(account.status)}
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {account.status}
                          </Typography>
                          {!isUsEast1EnabledForAccount && (
                            <Tooltip
                              title="us-east-1 is disabled for this account. This can affect monitoring and event-based features."
                              arrow
                            >
                              <WarningIcon color="warning" sx={{ fontSize: 20 }} />
                            </Tooltip>
                          )}
                          {account.cloudtrail === false && (
                            <Tooltip 
                              title="CloudTrail is not enabled. This affects event-based tagging features and may limit real-time resource monitoring capabilities."
                              arrow
                            >
                              <WarningIcon color="warning" sx={{ fontSize: 20 }} />
                            </Tooltip>
                          )}
                          {account.permissions === false && (
                            <Tooltip 
                              title="Account role permissions are missing or incorrect. Please update the role permissions to allow TagOps to scan and tag resources."
                              arrow
                            >
                              <WarningIcon color="warning" sx={{ fontSize: 20 }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <PermissionButton
                            permission="PowerUser"
                            size="small"
                            startIcon={<EditIcon />}
                            variant="outlined"
                              onClick={() => handleOpenEditDialog(account)}
                          >
                            Edit
                          </PermissionButton>
                          <PermissionButton
                            permission="PowerUser"
                            size="small"
                            startIcon={<DeleteIcon />}
                            variant="outlined"
                            color="error"
                              onClick={() => handleDeleteAccount(account)}
                          >
                            Remove
                          </PermissionButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Organizations Section */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Connected AWS Organizations
            </Typography>
          </Box>

          {loadingOrganizations ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : organizations.length === 0 ? (
            <Alert severity="info">
              No AWS organizations connected yet. Click the "Add Organization" button to add your first organization.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 48 }}></TableCell>
                    <TableCell>Organization Name</TableCell>
                    <TableCell>Management Account ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Show creating organizations */}
                  {Array.from(creatingOrganizations.entries()).map(([accountId, organizationName]) => (
                    <TableRow key={`creating-${accountId}`} hover>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {organizationName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {accountId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Creating
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Please wait...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Show deleting organizations */}
                  {Array.from(deletingOrganizations.entries()).map(([accountId, organizationName]) => (
                    <TableRow key={`deleting-${accountId}`} hover>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {organizationName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {accountId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Deleting
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Please wait...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Show existing organizations */}
                  {organizations.filter(org => {
                    const accountId = org.organizationManagementAccountId || org.type?.split('#')[1] || '';
                    return !creatingOrganizations.has(accountId) && !deletingOrganizations.has(accountId);
                  }).map((org) => {
                    const accountId = org.organizationManagementAccountId || org.type?.split('#')[1] || '';
                    const members = orgMembers[accountId] || [];
                    const isLoadingMembers = loadingOrgMembers[accountId] || false;
                    
                    const isExpanded = expandedOrganizations[accountId] || false;
                    const selected = selectedMembers[accountId] || new Set<string>();
                    const allSelected = areAllMembersSelected(accountId);
                    const someSelected = areSomeMembersSelected(accountId);
                    
                    return (
                      <React.Fragment key={accountId}>
                        <TableRow hover>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => toggleOrganizationExpansion(accountId)}
                            sx={{ p: 0.5 }}
                          >
                            {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {org.organization_name || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {accountId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getStatusIcon(org.status || 'active')}
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {org.status || 'active'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <PermissionButton
                              permission="PowerUser"
                              size="small"
                              startIcon={<EditIcon />}
                              variant="outlined"
                              onClick={() => handleOpenEditOrgDialog(org)}
                            >
                              Edit
                            </PermissionButton>
                            <Tooltip
                              title={
                                hasOnboardedMembers(accountId)
                                  ? "Cannot delete organization: One or more member accounts are onboarded. Please remove onboarded member accounts first."
                                  : "Delete organization"
                              }
                              arrow
                            >
                              <span>
                                <PermissionButton
                                  permission="PowerUser"
                                  size="small"
                                  startIcon={<DeleteIcon />}
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleDeleteOrganization(org)}
                                  disabled={hasOnboardedMembers(accountId)}
                                >
                                  Remove
                                </PermissionButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      {/* Member Accounts Table */}
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 0, border: 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 2, mb: 2, pl: 3, width: '98%' }}>
                              {isLoadingMembers ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                  <CircularProgress size={24} />
                                </Box>
                              ) : members.length > 0 ? (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      Member Accounts ({members.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                      <PermissionButton
                                        permission="PowerUser"
                                        variant="outlined"
                                        size="small"
                                        startIcon={bulkVerifyingMembers[accountId] ? <CircularProgress size={16} /> : null}
                                        onClick={() => handleBulkVerifyMembers(accountId)}
                                        disabled={selected.size === 0 || bulkVerifyingMembers[accountId]}
                                      >
                                        {bulkVerifyingMembers[accountId] ? 'Verifying...' : `Verify Selected (${selected.size})`}
                                      </PermissionButton>
                                      <PermissionButton
                                        permission="PowerUser"
                                        variant="contained"
                                        size="small"
                                        startIcon={bulkAddingMembers[accountId] ? <CircularProgress size={16} /> : <AddIcon />}
                                        onClick={() => handleBulkAddMembers(accountId)}
                                        disabled={selected.size === 0 || bulkAddingMembers[accountId]}
                                      >
                                        {bulkAddingMembers[accountId] ? 'Adding...' : `Add Selected (${selected.size})`}
                                      </PermissionButton>
                                      <PermissionButton
                                        permission="PowerUser"
                                        variant="outlined"
                                        size="small"
                                        color="error"
                                        startIcon={bulkDeletingMembers[accountId] ? <CircularProgress size={16} /> : <DeleteIcon />}
                                        onClick={() => handleBulkDeleteMembers(accountId)}
                                        disabled={selected.size === 0 || bulkDeletingMembers[accountId]}
                                      >
                                        {bulkDeletingMembers[accountId] ? 'Deleting...' : `Delete Selected (${selected.size})`}
                                      </PermissionButton>
                                    </Box>
                                  </Box>
                                  <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell padding="checkbox" sx={{ width: 48 }}>
                                            <Checkbox
                                              indeterminate={someSelected}
                                              checked={allSelected}
                                              onChange={(e) => handleSelectAllMembers(accountId, e.target.checked)}
                                              size="small"
                                            />
                                          </TableCell>
                                          <TableCell>Account Name</TableCell>
                                          <TableCell>Account ID</TableCell>
                                          <TableCell>Regions</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell>Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                    {members.map((memberAccountId) => {
                                      const isOnboarded = isMemberOnboarded(memberAccountId);
                                      const onboardedAccount = getOnboardedMemberAccount(memberAccountId);
                                      const isVerified = verifiedMembers[memberAccountId] || false;
                                      const isVerifying = verifyingMember[memberAccountId] || false;
                                      const isAdding = addingMemberAccount[memberAccountId] || false;
                                      const cloudtrailStatus = memberCloudTrailStatus[memberAccountId];
                                      // For onboarded accounts, try to get CloudTrail status from the account object if available
                                      const onboardedCloudtrailStatus = onboardedAccount ? (onboardedAccount as any).cloudtrail_enabled : null;
                                      const displayCloudtrailStatus = cloudtrailStatus !== undefined ? cloudtrailStatus : onboardedCloudtrailStatus;
                                      
                                      // Get regions from onboarded account only (from /account/list)
                                      let regionsList: string[] = [];
                                      let allowedRegionsList: string[] = [];
                                      if (isOnboarded && onboardedAccount) {
                                        try {
                                          if (Array.isArray(onboardedAccount.regions)) {
                                            regionsList = onboardedAccount.regions;
                                          } else if (typeof onboardedAccount.regions === 'string') {
                                            regionsList = JSON.parse(onboardedAccount.regions);
                                          }
                                          
                                          // Parse allowed regions
                                          const allowedField: any = (onboardedAccount as any).allowed_regions || (onboardedAccount as any).allowerd_regions;
                                          if (allowedField) {
                                            if (Array.isArray(allowedField)) {
                                              allowedRegionsList = allowedField;
                                            } else if (typeof allowedField === 'string') {
                                              allowedRegionsList = JSON.parse(allowedField);
                                            }
                                          }
                                        } catch (e) {
                                          console.error('Failed to parse regions', e);
                                        }
                                      }
                                      // For non-onboarded accounts, regionsList remains empty
                                      
                                      // Check if us-east-1 is enabled for onboarded accounts
                                      const isUsEast1EnabledForMemberAccount = 
                                        allowedRegionsList.length > 0 ? allowedRegionsList.includes('us-east-1') : true;
                                      
                                      const isMemberSelected = selected.has(memberAccountId);
                                      
                                      return (
                                        <TableRow key={memberAccountId} hover>
                                          <TableCell padding="checkbox">
                                            <Checkbox
                                              checked={isMemberSelected}
                                              onChange={(e) => handleMemberSelection(accountId, memberAccountId, e.target.checked)}
                                              size="small"
                                            />
                                          </TableCell>
                                          <TableCell>
                                            {isOnboarded && onboardedAccount ? (
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  cursor: 'pointer',
                                                  color: 'primary.main',
                                                  '&:hover': {
                                                    textDecoration: 'underline',
                                                    color: 'primary.dark'
                                                  }
                                                }}
                                                onClick={() => {
                                                  setSelectedAccountForDetails(onboardedAccount);
                                                  setShowAccountDetailsDialog(true);
                                                }}
                                              >
                                                {onboardedAccount.account_name}
                                              </Typography>
                                            ) : (
                                              <Typography variant="body2" color="text.secondary">
                                                {''}
                                              </Typography>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                              {memberAccountId}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                              {updatingAccounts.has(memberAccountId) || deletingAccounts.has(memberAccountId) ? (
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                  Processing...
                                                </Typography>
                                              ) : regionsList.length > 0 ? (
                                                regionsList.map((region) => (
                                                  <Chip
                                                    key={region}
                                                    label={region}
                                                    size="small"
                                                    variant="outlined"
                                                  />
                                                ))
                                              ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                  {isOnboarded ? 'None' : ''}
                                                </Typography>
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {updatingAccounts.has(memberAccountId) ? (
                                                <>
                                                  <CircularProgress size={16} />
                                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    Updating
                                                  </Typography>
                                                </>
                                              ) : deletingAccounts.has(memberAccountId) ? (
                                                <>
                                                  <CircularProgress size={16} />
                                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    Deleting
                                                  </Typography>
                                                </>
                                              ) : isOnboarded && onboardedAccount ? (
                                                <>
                                                  {getStatusIcon(onboardedAccount.status)}
                                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                    {onboardedAccount.status}
                                                  </Typography>
                                                  {!isUsEast1EnabledForMemberAccount && (
                                                    <Tooltip
                                                      title="us-east-1 is disabled for this account. This can affect monitoring and event-based features."
                                                      arrow
                                                    >
                                                      <WarningIcon color="warning" sx={{ fontSize: 20 }} />
                                                    </Tooltip>
                                                  )}
                                                  {(displayCloudtrailStatus === false || onboardedAccount.cloudtrail === false) && (
                                                    <Tooltip 
                                                      title="CloudTrail is not enabled. This affects event-based tagging features and may limit real-time resource monitoring capabilities."
                                                      arrow
                                                    >
                                                      <WarningIcon color="warning" sx={{ fontSize: 20 }} />
                                                    </Tooltip>
                                                  )}
                                                  {onboardedAccount.permissions === false && (
                                                    <Tooltip 
                                                      title="Account role permissions are missing or incorrect. Please update the role permissions to allow TagOps to scan and tag resources."
                                                      arrow
                                                    >
                                                      <WarningIcon color="warning" sx={{ fontSize: 20 }} />
                                                    </Tooltip>
                                                  )}
                                                </>
                                              ) : isVerified ? (
                                                <>
                                                  <Typography variant="body2" color="success.main">
                                                    Verified
                                                  </Typography>
                                                  {displayCloudtrailStatus === false && (
                                                    <Tooltip
                                                      title="CloudTrail is disabled for this account. This may affect event-based tagging features."
                                                      arrow
                                                    >
                                                      <WarningIcon color="warning" sx={{ fontSize: 20 }} />
                                                    </Tooltip>
                                                  )}
                                                </>
                                              ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                  Not onboarded
                                                </Typography>
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                              {updatingAccounts.has(memberAccountId) || deletingAccounts.has(memberAccountId) ? (
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                  Please wait...
                                                </Typography>
                                              ) : isOnboarded ? (
                                                <>
                                                  <PermissionButton
                                                    permission="PowerUser"
                                                    size="small"
                                                    startIcon={<EditIcon />}
                                                    variant="outlined"
                                                    onClick={() => {
                                                      if (onboardedAccount) {
                                                        handleOpenEditDialog(onboardedAccount);
                                                      }
                                                    }}
                                                  >
                                                    Edit
                                                  </PermissionButton>
                                                  <PermissionButton
                                                    permission="PowerUser"
                                                    size="small"
                                                    startIcon={<DeleteIcon />}
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => {
                                                      if (onboardedAccount) {
                                                        handleDeleteAccount(onboardedAccount);
                                                      }
                                                    }}
                                                  >
                                                    Remove
                                                  </PermissionButton>
                                                </>
                                              ) : (
                                                <>
                                                  <PermissionButton
                                                    permission="PowerUser"
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => verifyMemberAccount(accountId, memberAccountId, org)}
                                                    disabled={isVerifying || isVerified}
                                                    startIcon={isVerifying ? <CircularProgress size={16} /> : null}
                                                  >
                                                    {isVerifying ? 'Verifying...' : isVerified ? 'Verified' : 'Verify'}
                                                  </PermissionButton>
                                                  <Tooltip
                                                    title={
                                                      isExpired
                                                        ? "Your subscription has expired. Renew your subscription to add AWS accounts."
                                                        : isFreeTrial && accounts.length >= 1
                                                        ? "Free Trial is limited to 1 AWS Account. Upgrade your subscription to add more accounts."
                                                        : maxAccounts !== null && accounts.length >= maxAccounts
                                                        ? `You've reached the maximum of ${maxAccounts} AWS Account${maxAccounts === 1 ? '' : 's'} for your subscription plan.`
                                                        : !isVerified
                                                        ? "Please verify the account before adding"
                                                        : ""
                                                    }
                                                    arrow
                                                  >
                                                    <span>
                                                      <PermissionButton
                                                        permission="PowerUser"
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => handleOpenAddMemberDialog(memberAccountId)}
                                                        disabled={
                                                          !isVerified || 
                                                          isAdding ||
                                                          isExpired ||
                                                          (isFreeTrial && accounts.length >= 1) ||
                                                          (maxAccounts !== null && accounts.length >= maxAccounts)
                                                        }
                                                        startIcon={isAdding ? <CircularProgress size={16} /> : <AddIcon />}
                                                      >
                                                        {isAdding ? 'Adding...' : 'Add Account'}
                                                      </PermissionButton>
                                                    </span>
                                                  </Tooltip>
                                                </>
                                              )}
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </>
                              ) : (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                  No member accounts found for this organization.
                                </Alert>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add Account Dialog */}
      <Dialog 
        open={showAddDialog} 
        onClose={() => {
          // Prevent closing when adding account or clicking outside
          return false;
        }}
        maxWidth="md" 
        fullWidth
        disableEscapeKeyDown={true}
      >
        <DialogTitle>Add AWS Account</DialogTitle>
        <DialogContent>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account Name"
                value={accountFormData.accountName}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limit to 50 characters
                  const limitedValue = value.slice(0, 50);
                  setAccountFormData({ ...accountFormData, accountName: limitedValue });
                  validateField('accountName', limitedValue);
                }}
                placeholder="e.g., Production Account"
                error={!!(fieldTouched.accountName && validationErrors.accountName)}
                helperText={(fieldTouched.accountName && validationErrors.accountName) || "AWS account friendly name (1-50 characters)"}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="AWS Account ID"
                value={accountFormData.accountId}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow numbers and limit to 12 digits
                  const numericValue = value.replace(/[^0-9]/g, '').slice(0, 12);
                  setAccountFormData({ ...accountFormData, accountId: numericValue });
                  validateField('accountId', numericValue);
                }}
                placeholder="123456789012"
                error={!!(fieldTouched.accountId && validationErrors.accountId)}
                helperText={(fieldTouched.accountId && validationErrors.accountId) || "12-digit AWS account ID (numbers only)"}
                required
              />
            </Grid>

            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="IAM Role Name"
                  value={accountFormData.roleName}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Limit to 64 characters
                    const limitedValue = value.slice(0, 64);
                    setAccountFormData({ ...accountFormData, roleName: limitedValue });
                    validateField('roleName', limitedValue);
                  }}
                  placeholder="tagops-exec-account-role"
                  error={!!(fieldTouched.roleName && validationErrors.roleName)}
                  helperText={(fieldTouched.roleName && validationErrors.roleName) || "Cross-account IAM role name (1-64 characters)"}
                  required
                />
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => copyToClipboard(accountFormData.roleName, 'IAM Role Name')}
                  sx={{ height: 40, mt: -1 }}
                  title="Copy to clipboard"
                >
                  Copy
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="External ID"
                  value={accountFormData.externalId}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Limit to 256 characters
                    const limitedValue = value.slice(0, 256);
                    setAccountFormData({ ...accountFormData, externalId: limitedValue });
                    validateField('externalId', limitedValue);
                  }}
                  error={!!(fieldTouched.externalId && validationErrors.externalId)}
                  helperText={(fieldTouched.externalId && validationErrors.externalId) || "External ID for enhanced security (1-256 characters)"}
                  required
                />
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => copyToClipboard(accountFormData.externalId, 'External ID')}
                  sx={{ height: 40, mt: -1 }}
                  title="Copy to clipboard"
                >
                  Copy
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={handleGenerateExternalId}
                  sx={{ height: 40, mt: -1 }}
                  title="Generate new External ID"
                >
                  Generate
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                options={AWS_REGIONS}
                value={accountFormData.mainRegion || null}
                getOptionLabel={formatRegionLabel}
                onChange={(event, newValue) => {
                  const main = newValue || '';
                  setAccountFormData({
                    ...accountFormData,
                    mainRegion: main,
                    regions: accountFormData.regions.filter((r) => r !== main),
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Main Region (required)"
                    placeholder="Select region where you will deploy the CloudFormation stack"
                    helperText="The AWS region where you will create the CloudFormation stack. The CF link will open in this region."
                    required
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={AWS_REGIONS.filter((r) => r !== accountFormData.mainRegion)}
                value={accountFormData.regions}
                getOptionLabel={formatRegionLabel}
                onChange={(event, newValue) => {
                  setAccountFormData({ 
                    ...accountFormData, 
                    regions: newValue 
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Additional AWS Regions (Optional)"
                    placeholder="Search and select additional regions"
                    helperText="Regions to configure in the stack apart from the main region above. The main region is already included."
                  />
                )}
                renderTags={() => null}
                disableCloseOnSelect
              />
              {accountFormData.regions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Selected Additional Regions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {accountFormData.regions.map((region) => (
                      <Chip
                        key={region}
                        label={formatRegionLabel(region)}
                        size="small"
                        variant="outlined"
                        onDelete={() => {
                          setAccountFormData({
                            ...accountFormData,
                            regions: accountFormData.regions.filter((r) => r !== region)
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please log into your AWS account and click the "Provision CloudFormation Stack" button below, or copy the CF Link to the browser where you're logged in to your AWS account.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                <Button
                  variant="outlined"
                  onClick={openCloudFormationStack}
                  disabled={!isFormValid()}
                >
                  Provision CloudFormation Stack
                </Button>
                <Button
                  variant="outlined"
                  onClick={copyCloudFormationLink}
                  disabled={!isFormValid()}
                  size="small"
                >
                  Copy CF Link
                </Button>
                <Button
                  variant="outlined"
                  onClick={verifyAccount}
                  disabled={!isFormValid() || verifying}
                >
                  {verifying ? <CircularProgress size={24} /> : 'Verify Account'}
                </Button>
              </Box>
            </Grid>
            {verified && (
              <Grid item xs={12}>
                <Alert severity="success">Account verified successfully</Alert>
                {cloudtrailEnabled !== null && (
                  <Box sx={{ mt: 2 }}>
                    <Alert 
                      severity={cloudtrailEnabled ? "success" : "warning"}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        CloudTrail Status: {cloudtrailEnabled ? "Enabled" : "Not Found"}
                      </Typography>
                      <Typography variant="body2">
                        {cloudtrailEnabled 
                          ? "CloudTrail is enabled and actively logging"
                          : "No active CloudTrail trails found in any region"
                        }
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </Grid>
            )}
            {addingAccount && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  Adding account... This may take 10-20 seconds, please wait.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (!addingAccount) {
                setShowAddDialog(false);
                // Reset form data when canceling
                setAccountFormData({
                  accountName: '',
                  accountId: '',
                  roleName: 'tagops-exec-account-role',
                  externalId: generateRandomId(),
                  mainRegion: '',
                  regions: [],
                });
                setValidationErrors({
                  accountName: '',
                  accountId: '',
                  roleName: '',
                  externalId: '',
                });
                setFieldTouched({
                  accountName: false,
                  accountId: false,
                  roleName: false,
                  externalId: false,
                });
                setVerified(false);
                setCloudtrailEnabled(null);
                setVerifying(false);
              }
            }}
            disabled={addingAccount}
          >
            Cancel
          </Button>
          <PermissionButton
            permission="PowerUser"
            onClick={handleAddAccount}
            variant="contained"
            disabled={!verified || !isFormValid() || addingAccount}
            startIcon={addingAccount ? <CircularProgress size={20} /> : null}
          >
            {addingAccount ? 'Adding Account...' : 'Add Account'}
          </PermissionButton>
        </DialogActions>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog 
        open={showEditDialog} 
        onClose={() => {
          // Prevent closing when updating account or clicking outside
          return false;
        }}
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown={true}
      >
        <DialogTitle>Edit AWS Account</DialogTitle>
        <DialogContent>
          {/* Information Alert */}
          {(() => {
            const isMemberAccount = !!(currentAccount && ((currentAccount as any).organizationId !== undefined || (currentAccount as any).organization_id !== undefined));
            return (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  {isMemberAccount ? (
                    <>
                      <strong>Editing organization member account:</strong> You can only modify the account name. 
                      All other settings are managed through the organization configuration.
                    </>
                  ) : (
                    <>
                      <strong>Editing account configuration:</strong> You can modify the account name and IAM role name. 
                      The Account ID and External ID cannot be changed once set.
                    </>
                  )}
                </Typography>
              </Alert>
            );
          })()}
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {(() => {
              const isMemberAccount = !!(currentAccount && ((currentAccount as any).organizationId !== undefined || (currentAccount as any).organization_id !== undefined));
              return (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Account Name"
                      value={accountFormData.accountName}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Limit to 50 characters
                        const limitedValue = value.slice(0, 50);
                        setAccountFormData({ ...accountFormData, accountName: limitedValue });
                        validateField('accountName', limitedValue);
                      }}
                      placeholder="e.g., Production Account"
                      error={!!(fieldTouched.accountName && validationErrors.accountName)}
                      helperText={(fieldTouched.accountName && validationErrors.accountName) || "AWS account friendly name (1-50 characters)"}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="AWS Account ID"
                      value={accountFormData.accountId}
                      disabled
                      helperText="Account ID cannot be changed once set"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="IAM Role Name"
                      value={accountFormData.roleName}
                      onChange={(e) => {
                        if (!isMemberAccount) {
                          const value = e.target.value;
                          // Limit to 64 characters
                          const limitedValue = value.slice(0, 64);
                          setAccountFormData({ ...accountFormData, roleName: limitedValue });
                          validateField('roleName', limitedValue);
                        }
                      }}
                      placeholder="tagopsRole"
                      error={!!(fieldTouched.roleName && validationErrors.roleName)}
                      helperText={(fieldTouched.roleName && validationErrors.roleName) || "Cross-account IAM role name (1-64 characters)"}
                      required
                      disabled={isMemberAccount}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="External ID"
                      value={accountFormData.externalId}
                      onChange={(e) => setAccountFormData({ ...accountFormData, externalId: e.target.value })}
                      placeholder="unique-external-id"
                      helperText="External ID cannot be changed after account creation"
                      InputProps={{ readOnly: true }}
                      disabled
                    />
                  </Grid>
                  
                  {accountFormData.mainRegion && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Main Region"
                        value={formatRegionLabel(accountFormData.mainRegion)}
                        helperText="Region where the CloudFormation stack is deployed (cannot be changed)"
                        InputProps={{ readOnly: true }}
                        disabled
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={AWS_REGIONS.filter((r) => r !== accountFormData.mainRegion)}
                      value={accountFormData.regions}
                      getOptionLabel={formatRegionLabel}
                      onChange={(event, newValue) => {
                        if (!isMemberAccount) {
                          setAccountFormData({ 
                            ...accountFormData, 
                            regions: newValue 
                          });
                        }
                      }}
                      disabled={isMemberAccount}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Additional AWS Regions (Optional)"
                          placeholder="Search and select additional regions"
                          helperText="Regions configured in the stack apart from the main region above."
                        />
                      )}
                      renderTags={() => null}
                      disableCloseOnSelect
                    />
                    {accountFormData.regions.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Selected Additional Regions:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {accountFormData.regions.map((region) => (
                            <Chip
                              key={region}
                              label={formatRegionLabel(region)}
                              size="small"
                              variant="outlined"
                              onDelete={isMemberAccount ? undefined : () => {
                                setAccountFormData({
                                  ...accountFormData,
                                  regions: accountFormData.regions.filter((r) => r !== region)
                                });
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {!isMemberAccount && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Note:</strong> To update regions in the CloudFormation stack, you'll need to manually update the stack parameters in the AWS Console after clicking "Update CloudFormation Stack". The region selection here is for reference only.
                        </Typography>
                      </Alert>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                      <Button
                        variant="outlined"
                        onClick={openUpdateCloudFormationStack}
                        disabled={!isEditFormValid() || isMemberAccount}
                      >
                        Update CloudFormation Stack
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={copyUpdateCloudFormationLink}
                        disabled={!isEditFormValid() || isMemberAccount}
                        size="small"
                      >
                        Copy CF Link
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={verifyEditAccount}
                        disabled={!isEditFormValid() || editVerifying}
                      >
                        {editVerifying ? <CircularProgress size={24} /> : 'Verify Account'}
                      </Button>
                    </Box>
                  </Grid>
                </>
              );
            })()}
            {editVerified && (
              <Grid item xs={12}>
                <Alert severity="success">Account verified successfully</Alert>
                {editCloudtrailEnabled !== null && (
                  <Box sx={{ mt: 2 }}>
                    <Alert 
                      severity={editCloudtrailEnabled ? "success" : "warning"}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        CloudTrail Status: {editCloudtrailEnabled ? "Enabled" : "Not Found"}
                      </Typography>
                      <Typography variant="body2">
                        {editCloudtrailEnabled 
                          ? "CloudTrail is enabled and actively logging"
                          : "No active CloudTrail trails found in any region"
                        }
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </Grid>
            )}
            {updatingAccount && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  Updating account... Please wait.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (!updatingAccount) {
                setShowEditDialog(false);
                // Reset verification state when canceling
                setValidationErrors({
                  accountName: '',
                  accountId: '',
                  roleName: '',
                  externalId: '',
                });
                setFieldTouched({
                  accountName: false,
                  accountId: false,
                  roleName: false,
                  externalId: false,
                });
                setEditVerified(false);
                setEditCloudtrailEnabled(null);
                setEditVerifying(false);
              }
            }}
            disabled={updatingAccount}
          >
            Cancel
          </Button>
          <PermissionButton
            permission="PowerUser"
            onClick={handleEditAccount}
            variant="contained"
            disabled={!editVerified || !isEditFormValid() || updatingAccount}
            startIcon={updatingAccount ? <CircularProgress size={20} /> : null}
          >
            {updatingAccount ? 'Updating Account...' : 'Update Account'}
          </PermissionButton>
        </DialogActions>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog 
        open={showDeleteDialog} 
        onClose={() => {
          // Prevent closing when deleting account or clicking outside
          return false;
        }}
        disableEscapeKeyDown={true}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete AWS Account</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete account <strong>{accountToDelete?.account_name}</strong>?
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              This action will permanently delete:
            </Typography>
            <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
              <li>
                <Typography variant="body2">
                  The AWS account connection from TagOps
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  All scan configurations and settings for this account
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  All inventory data and resource information
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  All tagging history
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  All scheduled scans and automation rules
                </Typography>
              </li>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </Alert>
          
          {accountToDelete?.cloudformation_arn && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> You will need to manually delete the CloudFormation stack from your AWS account to complete the cleanup process.
              </Typography>
            </Alert>
          )}
          
          {deletingAccount && (
            <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ mr: 2 }} />
              Deleting account... Please wait.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (!deletingAccount) {
                setShowDeleteDialog(false);
                setAccountToDelete(null);
              }
            }}
            disabled={deletingAccount}
          >
            Cancel
          </Button>
          <PermissionButton 
            permission="PowerUser"
            onClick={confirmDeleteAccount} 
            variant="contained" 
            color="error"
            disabled={deletingAccount}
            startIcon={deletingAccount ? <CircularProgress size={20} /> : null}
          >
            {deletingAccount ? 'Deleting...' : 'Delete Account'}
          </PermissionButton>
        </DialogActions>
      </Dialog>

      {/* CloudFormation Cleanup Dialog */}
      <Dialog open={showCloudFormationDialog} onClose={dismissCloudFormationDialog}>
        <DialogTitle>CloudFormation Stack Cleanup</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Please delete the onboarding CloudFormation stack for account <strong>{deletedAccount?.account_name}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click the button below to open the CloudFormation console and manually delete the stack.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={dismissCloudFormationDialog}>Later</Button>
          <Button onClick={copyCloudFormationCleanupLink} variant="outlined" size="small">
            Copy CF Link
          </Button>
          <Button onClick={openCloudFormationCleanup} variant="contained">
            Delete CloudFormation Stack
          </Button>
        </DialogActions>
      </Dialog>

      {/* CloudFormation Cleanup Dialog for Organizations */}
      <Dialog open={showCloudFormationOrgDialog} onClose={dismissCloudFormationDialogOrg}>
        <DialogTitle>CloudFormation Stack Cleanup</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Please delete the onboarding CloudFormation stack for organization <strong>{deletedOrganization?.organization_name}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click the button below to open the CloudFormation console and manually delete the stack.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={dismissCloudFormationDialogOrg}>Later</Button>
          <Button onClick={copyCloudFormationCleanupLinkOrg} variant="outlined" size="small">
            Copy CF Link
          </Button>
          <Button onClick={openCloudFormationCleanupOrg} variant="contained">
            Delete CloudFormation Stack
          </Button>
        </DialogActions>
      </Dialog>

      {/* Account Details Dialog */}
      <Dialog
        open={showAccountDetailsDialog}
        onClose={() => setShowAccountDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Account Details: {selectedAccountForDetails?.account_name}
        </DialogTitle>
        <DialogContent>
          {selectedAccountForDetails && (() => {
            // Parse regions
            let regionsList: string[] = [];
            let allowedRegionsList: string[] = [];
            try {
              if (selectedAccountForDetails.regions) {
                if (Array.isArray(selectedAccountForDetails.regions)) {
                  regionsList = selectedAccountForDetails.regions;
                } else {
                  regionsList = JSON.parse(selectedAccountForDetails.regions);
                }
              }
              const allowedField: any = (selectedAccountForDetails as any).allowed_regions || (selectedAccountForDetails as any).allowerd_regions;
              if (allowedField) {
                if (Array.isArray(allowedField)) {
                  allowedRegionsList = allowedField;
                } else if (typeof allowedField === 'string') {
                  allowedRegionsList = JSON.parse(allowedField);
                }
              }
            } catch (e) {
              console.error('Failed to parse regions', e);
            }

            const isUsEast1Enabled = allowedRegionsList.length > 0 ? allowedRegionsList.includes('us-east-1') : true;
            const alerts: Array<{ severity: 'error' | 'warning' | 'info'; message: string }> = [];

            // Collect alerts
            if (!isUsEast1Enabled) {
              alerts.push({
                severity: 'warning',
                message: 'us-east-1 is disabled for this account. This can affect monitoring and event-based features.'
              });
            }
            if (selectedAccountForDetails.cloudtrail === false) {
              alerts.push({
                severity: 'warning',
                message: 'CloudTrail is not enabled. This affects event-based tagging features and may limit real-time resource monitoring capabilities.'
              });
            }
            if (selectedAccountForDetails.permissions === false) {
              alerts.push({
                severity: 'error',
                message: 'Account role permissions are missing or incorrect. Please update the role permissions to allow TagOps to scan and tag resources.'
              });
            }

            return (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Account ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                      {selectedAccountForDetails.accountId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Account Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedAccountForDetails.account_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Role Name (Used for Onboarding)
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedAccountForDetails.role_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      External ID
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {selectedAccountForDetails.externalId}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => copyToClipboard(selectedAccountForDetails.externalId, 'External ID')}
                        sx={{ minWidth: 'auto', p: 0.5 }}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Status
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {getStatusIcon(selectedAccountForDetails.status)}
                      <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                        {selectedAccountForDetails.status}
                      </Typography>
                    </Box>
                  </Grid>
                  {selectedAccountForDetails.onboarding_region && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Onboarding Region
                      </Typography>
                      <Typography variant="body1">
                        {selectedAccountForDetails.onboarding_region}
                      </Typography>
                    </Grid>
                  )}
                  {selectedAccountForDetails.cloudformation_arn && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        CloudFormation Stack ARN
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                        {selectedAccountForDetails.cloudformation_arn}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Regions
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {regionsList.length > 0 ? (
                        regionsList.map((region) => (
                          <Chip
                            key={region}
                            label={region}
                            size="small"
                            variant="outlined"
                            color={region === 'us-east-1' && !isUsEast1Enabled ? 'warning' : 'default'}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          None configured
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  {allowedRegionsList.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Allowed Regions
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {allowedRegionsList.map((region) => (
                          <Chip
                            key={region}
                            label={region}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  
                  {/* Alerts Section */}
                  {alerts.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Alerts
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {alerts.map((alert, index) => (
                          <Alert key={index} severity={alert.severity}>
                            {alert.message}
                          </Alert>
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAccountDetailsDialog(false)}>
            Close
          </Button>
          {selectedAccountForDetails && !(selectedAccountForDetails as any).organizationId && !(selectedAccountForDetails as any).organization_id && (
            <PermissionButton
              permission="PowerUser"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                setShowAccountDetailsDialog(false);
                handleOpenEditDialog(selectedAccountForDetails);
              }}
            >
              Edit Account
            </PermissionButton>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Organization Dialog */}
      <Dialog 
        open={showAddOrgDialog} 
        onClose={() => {
          if (!addingOrg) {
            setShowAddOrgDialog(false);
            setOrgFormData({
              organizationName: '',
              accountId: '',
              roleName: 'tagops-exec-role',
              externalId: generateRandomId(),
              regions: [],
              organizationalUnitIds: '',
              includeManagementAccount: 'false',
            });
            setOrgValidationErrors({
              organizationName: '',
              accountId: '',
              roleName: '',
              externalId: '',
            });
            setOrgFieldTouched({
              organizationName: false,
              accountId: false,
              roleName: false,
              externalId: false,
            });
            setVerifiedOrg(false);
            setVerifyingOrg(false);
          }
        }}
        maxWidth="md" 
        fullWidth
        disableEscapeKeyDown={addingOrg}
      >
        <DialogTitle>Add AWS Organization</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Organization Name"
                value={orgFormData.organizationName}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100);
                  setOrgFormData({ ...orgFormData, organizationName: value });
                  validateOrgField('organizationName', value);
                }}
                placeholder="e.g., Production Organization"
                error={!!(orgFieldTouched.organizationName && orgValidationErrors.organizationName)}
                helperText={(orgFieldTouched.organizationName && orgValidationErrors.organizationName) || "Organization friendly name (1-100 characters)"}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Management Account ID"
                value={orgFormData.accountId}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 12);
                  setOrgFormData({ ...orgFormData, accountId: value });
                  validateOrgField('accountId', value);
                }}
                placeholder="123456789012"
                error={!!(orgFieldTouched.accountId && orgValidationErrors.accountId)}
                helperText={(orgFieldTouched.accountId && orgValidationErrors.accountId) || "12-digit AWS management account ID (numbers only)"}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="IAM Role Name"
                  value={orgFormData.roleName}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 64);
                    setOrgFormData({ ...orgFormData, roleName: value });
                    validateOrgField('roleName', value);
                  }}
                  placeholder="tagops-exec-role"
                  error={!!(orgFieldTouched.roleName && orgValidationErrors.roleName)}
                  helperText={(orgFieldTouched.roleName && orgValidationErrors.roleName) || "Cross-account IAM role name (1-64 characters)"}
                  required
                />
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => copyToClipboard(orgFormData.roleName, 'IAM Role Name')}
                  sx={{ height: 40, mt: -1 }}
                  title="Copy to clipboard"
                >
                  Copy
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="External ID"
                  value={orgFormData.externalId}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 256);
                    setOrgFormData({ ...orgFormData, externalId: value });
                    validateOrgField('externalId', value);
                  }}
                  error={!!(orgFieldTouched.externalId && orgValidationErrors.externalId)}
                  helperText={(orgFieldTouched.externalId && orgValidationErrors.externalId) || "External ID for enhanced security (1-256 characters)"}
                  required
                />
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => copyToClipboard(orgFormData.externalId, 'External ID')}
                  sx={{ height: 40, mt: -1 }}
                  title="Copy to clipboard"
                >
                  Copy
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => {
                    setOrgFormData({
                      ...orgFormData,
                      externalId: generateRandomId()
                    });
                  }}
                  sx={{ height: 40, mt: -1 }}
                  title="Generate new External ID"
                >
                  Generate
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={AWS_REGIONS}
                value={orgFormData.regions}
                getOptionLabel={formatRegionLabel}
                onChange={(event, newValue) => {
                  setOrgFormData({ 
                    ...orgFormData, 
                    regions: newValue 
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="AWS Regions (Optional)"
                    placeholder="Search and select regions"
                    helperText="Select AWS regions to configure in the CloudFormation stack. If no regions are selected, the stack will be deployed only in the current region."
                  />
                )}
                renderTags={() => null}
                disableCloseOnSelect
              />
              {orgFormData.regions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Selected Regions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {orgFormData.regions.map((region) => (
                      <Chip
                        key={region}
                        label={formatRegionLabel(region)}
                        size="small"
                        variant="outlined"
                        onDelete={() => {
                          setOrgFormData({
                            ...orgFormData,
                            regions: orgFormData.regions.filter((r) => r !== region)
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mr: 0.5 }}>
                  Organizational Unit IDs
                </Typography>
                <Tooltip
                  title="Connect to org management account, Navigate to Organizations, the ou id will appear beneath each folder."
                  arrow
                  placement="top"
                >
                  <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                value={orgFormData.organizationalUnitIds}
                onChange={(e) => {
                  setOrgFormData({ ...orgFormData, organizationalUnitIds: e.target.value });
                }}
                placeholder="ou-xxxx-xxxxxxxx, ou-xxxx-yyyyyyyy or r-xxxx for root OU"
                helperText="Comma-separated list of Organizational Unit IDs (ou-xxxxx-xxxxxxxx) to deploy to. For full organization deployment, use the root OU ID (r-xxxx format)"
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={['false', 'true']}
                value={orgFormData.includeManagementAccount}
                onChange={(event, newValue) => {
                  setOrgFormData({ 
                    ...orgFormData, 
                    includeManagementAccount: newValue || 'false'
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Include Management Account"
                    helperText="Include the Management account in the deployment"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please log into your AWS management account and click the "Provision CloudFormation Stack" button below, or copy the CF Link to the browser where you're logged in to your AWS management account.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                <Button
                  variant="outlined"
                  onClick={openOrgCloudFormationStack}
                  disabled={!isOrgFormValid()}
                >
                  Provision CloudFormation Stack
                </Button>
                <Button
                  variant="outlined"
                  onClick={copyOrgCloudFormationLink}
                  disabled={!isOrgFormValid()}
                  size="small"
                >
                  Copy CF Link
                </Button>
                <Button
                  variant="outlined"
                  onClick={verifyOrganization}
                  disabled={!isOrgFormValid() || verifyingOrg}
                >
                  {verifyingOrg ? <CircularProgress size={24} /> : 'Verify Organization'}
                </Button>
              </Box>
            </Grid>
            {verifiedOrg && (
              <Grid item xs={12}>
                <Alert severity="success">Organization verified successfully</Alert>
              </Grid>
            )}
            {addingOrg && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  Adding organization... This may take a few seconds, please wait.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (!addingOrg) {
                setShowAddOrgDialog(false);
                setOrgFormData({
                  organizationName: '',
                  accountId: '',
                  roleName: 'tagops-exec-role',
                  externalId: generateRandomId(),
                  regions: [],
                  organizationalUnitIds: '',
                  includeManagementAccount: 'false',
                });
                setOrgValidationErrors({
                  organizationName: '',
                  accountId: '',
                  roleName: '',
                  externalId: '',
                });
                setOrgFieldTouched({
                  organizationName: false,
                  accountId: false,
                  roleName: false,
                  externalId: false,
                });
                setVerifiedOrg(false);
                setVerifyingOrg(false);
              }
            }}
            disabled={addingOrg}
          >
            Cancel
          </Button>
          <PermissionButton
            permission="PowerUser"
            onClick={handleAddOrganization}
            variant="contained"
            disabled={!verifiedOrg || !isOrgFormValid() || addingOrg}
            startIcon={addingOrg ? <CircularProgress size={20} /> : null}
          >
            {addingOrg ? 'Adding Organization...' : 'Add Organization'}
          </PermissionButton>
        </DialogActions>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog 
        open={showEditOrgDialog} 
        onClose={() => {
          if (!updatingOrg) {
            setShowEditOrgDialog(false);
            setOrgValidationErrors({
              organizationName: '',
              accountId: '',
              roleName: '',
              externalId: '',
            });
            setOrgFieldTouched({
              organizationName: false,
              accountId: false,
              roleName: false,
              externalId: false,
            });
            setEditVerifiedOrg(false);
            setEditVerifyingOrg(false);
          }
        }}
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown={updatingOrg}
      >
        <DialogTitle>Edit AWS Organization</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Editing organization configuration:</strong> You can modify the organization name, IAM role name, and Organizational Unit IDs. 
              The Management Account ID and External ID cannot be changed once set.
            </Typography>
          </Alert>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Organization Name"
                value={orgFormData.organizationName}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100);
                  setOrgFormData({ ...orgFormData, organizationName: value });
                  validateOrgField('organizationName', value);
                }}
                placeholder="e.g., Production Organization"
                error={!!(orgFieldTouched.organizationName && orgValidationErrors.organizationName)}
                helperText={(orgFieldTouched.organizationName && orgValidationErrors.organizationName) || "Organization friendly name (1-100 characters)"}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Management Account ID"
                value={orgFormData.accountId}
                disabled
                helperText="Management Account ID cannot be changed once set"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="IAM Role Name"
                value={orgFormData.roleName}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 64);
                  setOrgFormData({ ...orgFormData, roleName: value });
                  validateOrgField('roleName', value);
                }}
                placeholder="tagops-exec-role"
                error={!!(orgFieldTouched.roleName && orgValidationErrors.roleName)}
                helperText={(orgFieldTouched.roleName && orgValidationErrors.roleName) || "Cross-account IAM role name (1-64 characters)"}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="External ID"
                value={orgFormData.externalId}
                helperText="External ID cannot be changed after organization creation"
                InputProps={{ readOnly: true }}
                disabled
              />
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={AWS_REGIONS}
                value={orgFormData.regions}
                getOptionLabel={formatRegionLabel}
                onChange={(event, newValue) => {
                  setOrgFormData({ 
                    ...orgFormData, 
                    regions: newValue 
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="AWS Regions (Optional)"
                    placeholder="Search and select regions"
                    helperText="Select AWS regions to configure in the CloudFormation stack. If no regions are selected, the stack will be deployed only in the current region."
                  />
                )}
                renderTags={() => null}
                disableCloseOnSelect
              />
              {orgFormData.regions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Selected Regions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {orgFormData.regions.map((region) => (
                      <Chip
                        key={region}
                        label={formatRegionLabel(region)}
                        size="small"
                        variant="outlined"
                        onDelete={() => {
                          setOrgFormData({
                            ...orgFormData,
                            regions: orgFormData.regions.filter((r) => r !== region)
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> To update regions in the CloudFormation stack, you'll need to manually update the stack parameters in the AWS Console after clicking "Update CloudFormation Stack". The region selection here is for reference only.
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mr: 0.5 }}>
                  Organizational Unit IDs
                </Typography>
                <Tooltip
                  title="Connect to org management account, Navigate to Organizations, the OU ids will appear beneath each folder."
                  arrow
                  placement="top"
                >
                  <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                value={orgFormData.organizationalUnitIds}
                onChange={(e) => {
                  setOrgFormData({ ...orgFormData, organizationalUnitIds: e.target.value });
                }}
                placeholder="ou-xxxx-xxxxxxxx, ou-xxxx-yyyyyyyy or r-xxxx for root OU"
                helperText="Comma-separated list of Organizational Unit IDs (ou-xxxxx-xxxxxxxx) to deploy to. For full organization deployment, use the root OU ID (r-xxxx format)"
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={['false', 'true']}
                value={orgFormData.includeManagementAccount}
                onChange={(event, newValue) => {
                  setOrgFormData({ 
                    ...orgFormData, 
                    includeManagementAccount: newValue || 'false'
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Include Management Account"
                    helperText="Include the Management account in the deployment"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                <Button
                  variant="outlined"
                  onClick={openUpdateOrgCloudFormationStack}
                  disabled={!currentOrg || !currentOrg.cloudformation_arn || !currentOrg.onboarding_region}
                >
                  Update CloudFormation Stack
                </Button>
                <Button
                  variant="outlined"
                  onClick={copyUpdateOrgCloudFormationLink}
                  disabled={!currentOrg || !currentOrg.cloudformation_arn || !currentOrg.onboarding_region}
                  size="small"
                >
                  Copy CF Link
                </Button>
                <Button
                  variant="outlined"
                  onClick={verifyEditOrganization}
                  disabled={!isEditOrgFormValid() || editVerifyingOrg}
                >
                  {editVerifyingOrg ? <CircularProgress size={24} /> : 'Verify Organization'}
                </Button>
              </Box>
            </Grid>
            {editVerifiedOrg && (
              <Grid item xs={12}>
                <Alert severity="success">Organization verified successfully</Alert>
              </Grid>
            )}
            {updatingOrg && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  Updating organization... Please wait.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (!updatingOrg) {
                setShowEditOrgDialog(false);
                setOrgValidationErrors({
                  organizationName: '',
                  accountId: '',
                  roleName: '',
                  externalId: '',
                });
                setOrgFieldTouched({
                  organizationName: false,
                  accountId: false,
                  roleName: false,
                  externalId: false,
                });
                setEditVerifiedOrg(false);
                setEditVerifyingOrg(false);
              }
            }}
            disabled={updatingOrg}
          >
            Cancel
          </Button>
          <PermissionButton
            permission="PowerUser"
            onClick={handleEditOrganization}
            variant="contained"
            disabled={!editVerifiedOrg || !isEditOrgFormValid() || updatingOrg}
            startIcon={updatingOrg ? <CircularProgress size={20} /> : null}
          >
            {updatingOrg ? 'Updating Organization...' : 'Update Organization'}
          </PermissionButton>
        </DialogActions>
      </Dialog>

      {/* Delete Organization Confirmation Dialog */}
      <Dialog 
        open={showDeleteOrgDialog} 
        onClose={() => {
          if (!deletingOrg) {
            setShowDeleteOrgDialog(false);
            setOrgToDelete(null);
          }
        }}
        disableEscapeKeyDown={deletingOrg}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete AWS Organization</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete organization <strong>{orgToDelete?.organization_name}</strong>?
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              This action will permanently delete:
            </Typography>
            <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
              <li>
                <Typography variant="body2">
                  The AWS organization connection from TagOps
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  All organization configuration and settings
                </Typography>
              </li>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </Alert>
          
          {deletingOrg && (
            <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ mr: 2 }} />
              Deleting organization... Please wait.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (!deletingOrg) {
                setShowDeleteOrgDialog(false);
                setOrgToDelete(null);
              }
            }}
            disabled={deletingOrg}
          >
            Cancel
          </Button>
          <PermissionButton 
            permission="PowerUser"
            onClick={confirmDeleteOrganization} 
            variant="contained" 
            color="error"
            disabled={deletingOrg}
            startIcon={deletingOrg ? <CircularProgress size={20} /> : null}
          >
            {deletingOrg ? 'Deleting...' : 'Delete Organization'}
          </PermissionButton>
        </DialogActions>
      </Dialog>

      {/* Add Member Account Dialog */}
      <Dialog 
        open={showAddMemberDialog} 
        onClose={() => {
          if (!addingMemberAccount[currentMemberAccountId || '']) {
            setShowAddMemberDialog(false);
            setCurrentMemberAccountId(null);
          }
        }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Add Member Account</DialogTitle>
        <DialogContent>
          {currentMemberAccountId && memberFormData[currentMemberAccountId] && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Name"
                  value={memberFormData[currentMemberAccountId].accountName}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 50);
                    setMemberFormData(prev => ({
                      ...prev,
                      [currentMemberAccountId]: {
                        ...prev[currentMemberAccountId],
                        accountName: value
                      }
                    }));
                  }}
                  placeholder="e.g., Production Account"
                  helperText="AWS account friendly name (1-50 characters)"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account ID"
                  value={memberFormData[currentMemberAccountId].accountId}
                  disabled
                  helperText="Account ID from organization"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Role Name"
                  value={memberFormData[currentMemberAccountId].roleName}
                  disabled
                  helperText="Role name from organization"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="External ID"
                  value={memberFormData[currentMemberAccountId].externalId}
                  disabled
                  helperText="External ID from organization"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (!addingMemberAccount[currentMemberAccountId || '']) {
                setShowAddMemberDialog(false);
                setCurrentMemberAccountId(null);
              }
            }}
            disabled={!!addingMemberAccount[currentMemberAccountId || '']}
          >
            Cancel
          </Button>
          <PermissionButton
            permission="PowerUser"
            onClick={handleAddMemberAccount}
            variant="contained"
            disabled={!currentMemberAccountId || !memberFormData[currentMemberAccountId]?.accountName || !!addingMemberAccount[currentMemberAccountId || '']}
            startIcon={currentMemberAccountId && addingMemberAccount[currentMemberAccountId] ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {currentMemberAccountId && addingMemberAccount[currentMemberAccountId] ? 'Adding Account...' : 'Add Account'}
          </PermissionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

